let SubmitTitle = class {
  constructor () {
    console.log('SubmitTitle constructor called');

    // Form data
    this.formData = {};

    // Wizard instance
    this.wizard = null;

    // Form validator instance
    this.validator = null;

    // IMDB prompt flag
    this.imdbPromptShown = false;

    // IMDB confirmation flag - tracks if user has confirmed IMDB data
    this.imdbConfirmed = false;

    // Auto-save instance
    this.autoSave = null;

    // CAP proof-of-work state
    this.capSolver = null;

    // Initialize element references
    this.el = this.createElementReferences();

    // Initialize your module
    this.bindEvents();
    this.initialize();
  }

  createElementReferences () {
    const container = document.querySelector('#titleSubmissionContainer');
    if (!container) {
      console.error('Container not found:', '#titleSubmissionContainer');
      return {};
    }

    console.log('Container found:', container);
    console.log('Looking for step-wizard:', container.querySelector('.step-wizard'));

    return {
      container: container,
      form: container.querySelector('#submissionForm'),
      stepWizard: container.querySelector('.step-wizard'),
      stepCards: container.querySelectorAll('.step-card'),

      // Step 1 elements
      productionPhaseRadios: container.querySelectorAll('input[name="production_phase"]'),
      postProductionNote: container.querySelector('#postProductionNote'),
      otherProductionNote: container.querySelector('#otherProductionNote'),

      // Step 2 elements
      imdbLink: container.querySelector('#imdbLink'),
      fetchImdbBtn: container.querySelector('#fetchImdb'),
      manualEntryFields: container.querySelector('#manualEntryFields'),

      // Step 1 elements (platforms moved here)
      desiredPlatformOther: container.querySelector('input[name="desired_platform_other"]'),
      featureTypeRadios: container.querySelectorAll('input[name="feature_type"]'),
      featureTypeOther: container.querySelector('input[name="feature_type_other"]'),

      // Step 4 elements
      territoriesSelect: container.querySelector('select[name="territories"]'),
      territoriesOther: container.querySelector('input[name="territories_other"]'),
      availableRightsRadios: container.querySelectorAll('input[name="available_rights"]'),
      rightsSomeDetails: container.querySelector('textarea[name="rights_some_details"]'),

      // Navigation buttons - look for buttons with these classes
      nextButtons: container.querySelectorAll('.button-next, .button-primary.button-next, button[type="button"].button-next'),
      prevButtons: container.querySelectorAll('.button-prev, .button-secondary.button-prev, button[type="button"].button-prev'),

      // File upload areas
      fileUploadAreas: container.querySelectorAll('.file-upload-area'),
    };
  }

  bindEvents () {
    // Initialize multiselect for platform dropdown
    this.initializePlatformMultiselect();

    // Navigation events - use event delegation for better handling
    this.el.form.addEventListener('click', (e) => {
      const btn = e.target.closest('.button-next:not(.button-force-next)');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();

        // Check if button is visually disabled
        if (btn.classList.contains('disabled')) {
          // Force validation to show errors
          const currentStep = this.wizard ? this.wizard.getCurrentStep() : 1;
          this.validateStep(currentStep, true);
          this.showNotification('Please fill in all required fields before proceeding.', 'error');
        } else {
          this.handleNext();
        }
      }

      // Handle submit button clicks when disabled
      const submitBtn = e.target.closest('button[type="submit"]');
      if (submitBtn && submitBtn.classList.contains('disabled')) {
        e.preventDefault();
        e.stopPropagation();

        // Force validation to show errors for step 6
        this.validateStep(6, true);
        this.showNotification('Please fill in all required fields before submitting.', 'error');
      }
    });

    if (this.el.prevButtons) {
      this.el.prevButtons.forEach(btn => {
        btn.addEventListener('click', () => this.handlePrev());
      });
    }

    // Form submission
    if (this.el.form) {
      this.el.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    // Force navigation buttons for dev
    const forcePrevButtons = this.el.container.querySelectorAll('.button-force-prev');
    forcePrevButtons.forEach(btn => {
      btn.addEventListener('click', () => this.handleForcePrev());
    });

    const forceNextButtons = this.el.container.querySelectorAll('.button-force-next');
    forceNextButtons.forEach(btn => {
      btn.addEventListener('click', () => this.handleForceNext());
    });

    // Dev fill button
    const devFillBtn = this.el.container.querySelector('.button-dev-fill');
    if (devFillBtn) {
      devFillBtn.addEventListener('click', () => this.fillWithFakeData());
    }

    // Dev copy prefill button
    const devCopyPrefillBtn = this.el.container.querySelector('.button-dev-copy-prefill');
    if (devCopyPrefillBtn) {
      devCopyPrefillBtn.addEventListener('click', () => this.copyPrefillUrl());
    }

    // Dev navigation buttons
    const devPrevBtn = this.el.container.querySelector('.button-dev-prev');
    if (devPrevBtn) {
      devPrevBtn.addEventListener('click', () => this.handleForcePrev());
    }

    const devNextBtn = this.el.container.querySelector('.button-dev-next');
    if (devNextBtn) {
      devNextBtn.addEventListener('click', () => this.handleForceNext());
    }

    // Step 1 - Production phase changes
    this.el.productionPhaseRadios?.forEach(radio => {
      radio.addEventListener('change', (e) => this.handleProductionPhaseChange(e));
    });

    // Bind field validation events
    this.bindFieldValidation();

    // Step 2 - IMDB fetch - direct fetch without prompt
    this.el.fetchImdbBtn?.addEventListener('click', () => this.fetchImdbData());

    // Bind Enter key on IMDB link input to trigger fetch
    this.el.imdbLink?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.fetchImdbData();
      }
    });

    // Auto-prefill when valid IMDB link is entered
    let imdbFetchTimeout;
    this.isImdbFetching = false; // Track fetching state

    this.el.imdbLink?.addEventListener('input', (e) => {
      let imdbUrl = e.target.value.trim();

      // Auto-format IMDB ID to full URL if user enters just the ID
      if (imdbUrl && imdbUrl.match(/^tt\d+$/i)) {
        imdbUrl = `https://www.imdb.com/title/${imdbUrl}/`;
        e.target.value = imdbUrl;
      }

      // Clear any existing timeout to debounce
      if (imdbFetchTimeout) {
        clearTimeout(imdbFetchTimeout);
      }

      // Clear any existing error messages
      const existingError = e.target.parentNode.parentNode.querySelector('.field-error-message');
      if (existingError) {
        existingError.remove();
      }

      // If valid IMDB URL, auto-fetch after a short delay
      if (imdbUrl && this.isValidImdbUrl(imdbUrl) && !this.isImdbFetching) {
        // Add loading indicator
        this.el.fetchImdbBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Loading...';
        this.el.fetchImdbBtn.disabled = true;

        imdbFetchTimeout = setTimeout(() => {
          this.fetchImdbDataSilently();
        }, 800); // Wait 800ms after user stops typing
      } else if (!imdbUrl || !this.isValidImdbUrl(imdbUrl)) {
        // Reset button if field is cleared or invalid
        this.el.fetchImdbBtn.innerHTML = 'Import Data';
        this.el.fetchImdbBtn.disabled = false;
        this.isImdbFetching = false;
      }
    });

    // Manual entry fields are now always visible - no toggle needed

    // Clear error states on input
    this.el.container.addEventListener('input', (e) => {
      if (e.target.matches('input, textarea, select')) {
        this.clearFieldError(e.target);
      }
    });

    // Clear error state on genre selection
    this.el.container.addEventListener('change', (e) => {
      if (e.target.name === 'genres[]' || e.target.id === 'genres-select') {
        const genresContainer = this.el.container.querySelector('#genres-container');
        if (genresContainer) {
          const selectedGenres = $('#genres-select').val() || [];
          if (selectedGenres.length > 0) {
            this.clearMultiselectError(genresContainer);
          }
        }
      }
    });

    // Step 4 - Territory change
    this.el.territoriesSelect?.addEventListener('change', (e) => {
      if (this.el.territoriesOther) {
        this.el.territoriesOther.classList.toggle('hidden', e.target.value !== 'other');
        if (e.target.value === 'other') {
          this.el.territoriesOther.setAttribute('required', 'required');
        } else {
          this.el.territoriesOther.removeAttribute('required');
        }
      }
    });

    // Step 1 - Feature type radio change
    if (this.el.featureTypeRadios) {
      this.el.featureTypeRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          if (this.el.featureTypeOther) {
            this.el.featureTypeOther.classList.toggle('hidden', e.target.value !== 'other');
            if (e.target.value === 'other') {
              this.el.featureTypeOther.setAttribute('required', 'required');
            } else {
              this.el.featureTypeOther.removeAttribute('required');
              this.el.featureTypeOther.value = '';
            }
          }
        });
      });
    }

    // Step 1 - Platform checkbox change (moved from step 4)
    const platformOtherCheckbox = this.el.container.querySelector('#platform-other');
    if (platformOtherCheckbox) {
      platformOtherCheckbox.addEventListener('change', (e) => {
        if (this.el.desiredPlatformOther) {
          this.el.desiredPlatformOther.classList.toggle('hidden', !e.target.checked);
          if (e.target.checked) {
            this.el.desiredPlatformOther.setAttribute('required', 'required');
          } else {
            this.el.desiredPlatformOther.removeAttribute('required');
          }
        }
      });
    }

    // Clear platform error when any platform is selected
    this.el.container.addEventListener('change', (e) => {
      if (e.target.name === 'desired_platform[]') {
        const platformGroup = e.target.closest('.checkbox-group');
        if (platformGroup) {
          const errorMsg = platformGroup.parentNode.querySelector('.field-error-message');
          if (errorMsg) {
            errorMsg.remove();
          }
        }
      }
    });

    // Step 4 - Rights change
    this.el.availableRightsRadios?.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (this.el.rightsSomeDetails) {
          this.el.rightsSomeDetails.classList.toggle('hidden', e.target.value !== 'some');
        }
      });
    });

    // File upload areas - wait for FileUpload component to be initialized
    // The FileUpload component is initialized by file-upload.js on DOMContentLoaded
    // We need to wait a bit to avoid double click handlers
    setTimeout(() => {
      this.el.fileUploadAreas?.forEach(area => {
        console.log('Setting up file upload area:', area.dataset.inputName);

        // Always add event listeners regardless of FileUpload component status
        // These events should bubble up from the FileUpload component

        // Listen to file selection
        area.addEventListener('fileselect', (e) => {
          const files = e.detail.files;
          if (files && files.length > 0) {
            console.log('Files selected:', files.length);
          }
        });

        // Listen for upload completion
        area.addEventListener('fileuploaded', (e) => {
          console.log('File uploaded event received. Event detail:', e.detail);

          const { file } = e.detail;

          // Handle different event structures from file-upload.js
          // Some events have 'response', others have 'result'
          let uploadData = e.detail.response || e.detail.result;

          // If uploadData is still nested, extract it
          if (uploadData && (uploadData.data || uploadData.response)) {
            uploadData = uploadData.data || uploadData.response;
          }

          console.log('Extracted upload data:', uploadData);
          console.log('File name:', file?.name);

          // Store the upload result
          const inputName = area.dataset.inputName || 'file';

          // Validate that we have actual data to store
          if (!uploadData || (typeof uploadData === 'object' && !uploadData.file_id)) {
            console.error('Invalid upload data received:', uploadData);
            console.error('Full event detail was:', e.detail);
            this.showNotification('File uploaded but data was not properly captured. Please try again.', 'warning');
            return;
          }

          // Initialize uploadedFiles if not exists
          if (!this.uploadedFiles) {
            this.uploadedFiles = {};
            console.log('Initialized uploadedFiles object');
          }

          // For multiple files, store as array
          if (!this.uploadedFiles[inputName]) {
            this.uploadedFiles[inputName] = [];
            console.log(`Created array for ${inputName}`);
          }

          this.uploadedFiles[inputName].push(uploadData);
          console.log(`Stored upload data for ${inputName}:`, this.uploadedFiles[inputName]);
          console.log('Current uploadedFiles state:', this.uploadedFiles);
          console.log('Stored file_id:', uploadData.file_id);

          // Update link field requirements
          this.updateLinkRequirements();

          this.showNotification('File uploaded successfully!', 'success');
        });

        // Listen for file removal
        area.addEventListener('fileremove', (e) => {
          const { files } = e.detail;
          const inputName = area.dataset.inputName || 'file';

          console.log('File remove event received:', inputName, 'Files remaining:', files.length);

          // Always update uploadedFiles based on current state
          if (files.length === 0) {
            // No files left, remove from uploadedFiles
            if (this.uploadedFiles && this.uploadedFiles[inputName]) {
              console.log('Removing uploaded file record for:', inputName);
              delete this.uploadedFiles[inputName];
              console.log('After removal, uploadedFiles state:', this.uploadedFiles);
            }

            // Clear validation and success state from the file upload area
            area.classList.remove('is-valid', 'is-invalid', 'success', 'has-file');

            // Also clear any validation state from child elements
            const uploadIcon = area.querySelector('.upload-icon');
            if (uploadIcon) {
              uploadIcon.classList.remove('is-valid', 'is-invalid');
            }
          } else {
            // Update with remaining files - but don't reassign the entire object
            if (!this.uploadedFiles) {
              this.uploadedFiles = {};
            }
            this.uploadedFiles[inputName] = files;
            console.log('After file removal, uploadedFiles state:', this.uploadedFiles);
          }

          // Update link field requirements
          this.updateLinkRequirements();

          // Also trigger validation update to refresh button states
          const currentStep = this.wizard ? this.wizard.getCurrentStep() : 1;
          if (currentStep === 3) {
            // Re-validate step 3 to update button states and field requirements
            this.validateStep(3, false);

            // Force update the Next button state
            const nextBtn = this.el.container.querySelector('.wizard-next');
            if (nextBtn) {
              const validation = this.validateStep(3, false);
              nextBtn.disabled = !validation.valid;
            }
          }
        });

        // Check if FileUpload component already exists
        if (area.fileUpload) {
          console.log('FileUpload component already initialized for:', area.dataset.inputName);
        } else {
          console.log('FileUpload component not yet initialized for:', area.dataset.inputName);
          // The event listeners we added above should still work when the component initializes
        }
      });
    }, 300); // Wait 300ms for FileUpload to initialize

    // Toggle link/password fields functionality
    this.el.container.querySelectorAll('.toggle-link-fields').forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = button.getAttribute('data-target');
        const targetContainer = document.getElementById(targetId);

        if (targetContainer) {
          const isHidden = targetContainer.classList.contains('hidden');

          if (isHidden) {
            // Hide button with slide up animation
            button.classList.add('sliding-up');

            // Show content after button starts sliding
            setTimeout(() => {
              targetContainer.classList.remove('hidden');
              button.setAttribute('aria-expanded', 'true');
            }, 100);

            // Remove button from DOM after animation
            setTimeout(() => {
              button.style.display = 'none';
            }, 400);
          }
        }
      });
    });

    // Check if link/password fields have values on page load and show them if they do
    this.checkAndShowLinkFields();
  }

  bindFieldValidation () {
    // Initialize the form validator with custom options
    this.validator = new FormValidator(this.el.form, {
      autoValidate: true,
      validateOnBlur: true,
      validateOnInput: false,  // Don't validate on every input, wait for blur
      skipNoValidation: true,
      showSuccessState: true,
      scrollToError: true,
      fieldLabels: {
        'genres[]': 'Genres',
        'desired_platform[]': 'Which platforms would you like ',
        'territories': 'Territories',
        'available_rights': 'Available rights',
      },
    });
  }

  validateField (field) {
    // Delegate to the validator if it exists
    if (this.validator) {
      return this.validator.validateField(field);
    }
    return true;
  }

  isValidEmail (email) {
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  showImdbConfirmation (data) {
    // Hide any existing non-movie message first
    const existingNonMovieMessage = document.getElementById('non-movie-message');
    if (existingNonMovieMessage) {
      existingNonMovieMessage.remove();
    }

    const confirmationSection = document.getElementById('imdb-confirmation');
    const imdbTitle = document.getElementById('imdb-title');
    const imdbPoster = document.getElementById('imdb-poster');
    const posterPlaceholder = document.querySelector('.imdb-poster-placeholder');

    // Get the entire IMDB section (heading + input) to hide it
    const imdbInputGroup = this.el.imdbLink?.closest('.form-group');
    // Also get the heading elements above the input
    const imdbHeading = document.querySelector('.cell.medium-6 h3');
    const imdbSubheading = document.querySelector('.cell.medium-6 h5');

    if (!confirmationSection || !imdbTitle) {
      console.log('IMDB confirmation elements not found');
      return;
    }

    // Set the title
    if (data.title) {
      imdbTitle.textContent = data.title;
    } else {
      imdbTitle.textContent = 'Content Title';
    }

    // Handle poster image
    if (data.posterUrl && imdbPoster && posterPlaceholder) {
      imdbPoster.src = data.posterUrl;
      imdbPoster.style.display = 'block';
      posterPlaceholder.style.display = 'none';

      // Handle image load error
      imdbPoster.onerror = function() {
        this.style.display = 'none';
        posterPlaceholder.style.display = 'flex';
      };
    } else if (posterPlaceholder) {
      posterPlaceholder.style.display = 'flex';
      if (imdbPoster) {
        imdbPoster.style.display = 'none';
      }
    }

    // Hide the IMDB input group and headings
    if (imdbInputGroup) {
      imdbInputGroup.classList.add('hidden');
    }
    if (imdbHeading) {
      imdbHeading.classList.add('hidden');
    }
    if (imdbSubheading) {
      imdbSubheading.classList.add('hidden');
    }

    // Show the confirmation section
    confirmationSection.classList.remove('hidden');

    // Add event listeners for Yes/No buttons
    this.setupImdbConfirmationButtons();

    console.log('IMDB confirmation displayed with title:', data.title, 'and poster:', data.posterUrl);
  }

  showNonMovieMessage (supportEmail) {
    // Hide any existing IMDB confirmation
    const confirmationSection = document.getElementById('imdb-confirmation');
    if (confirmationSection) {
      confirmationSection.classList.add('hidden');
    }

    // Remove any existing non-movie message
    const existingMessage = document.getElementById('non-movie-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create the inline message similar to IMDB confirmation
    const messageContainer = document.createElement('div');
    messageContainer.id = 'non-movie-message';
    messageContainer.className = 'callout warning has-icon mt-3';
    messageContainer.innerHTML = `
      <div class="callout-content">
        <h4>Self-service is currently available only for Movies and Shorts</h4>
        <p>
          Our automated process currently only works for movies. Please reach out to our distribution team to submit other types of content.
        </p>
        <div>
          <a href="mailto:${supportEmail}" class="button display-inline">Contact distribution team</a>
          or 
          <button type="button" class="button secondary" id="non-movie-clear">Submit a different film</button>
        </div>
      </div>
    `;

    // Insert after the IMDB input group
    const imdbGroup = this.el.imdbLink.closest('.form-group');
    if (imdbGroup && imdbGroup.parentNode) {
      imdbGroup.parentNode.insertBefore(messageContainer, imdbGroup.nextSibling);
    }

    // Add event listeners for buttons
    document.getElementById('non-movie-clear')?.addEventListener('click', () => {
      // Clear the IMDB field
      if (this.el.imdbLink) {
        this.el.imdbLink.value = '';
        this.el.imdbLink.removeAttribute('data-touched');
        this.clearFieldError(this.el.imdbLink);
      }

      // Remove the message
      messageContainer.remove();

      // Hide manual entry fields
      if (this.el.manualEntryFields) {
        this.el.manualEntryFields.classList.add('hidden');
      }
    });

    document.getElementById('non-movie-continue')?.addEventListener('click', () => {
      // Remove the message
      messageContainer.remove();

      // Show manual entry fields
      if (this.el.manualEntryFields) {
        this.el.manualEntryFields.classList.remove('hidden');
      }

      // Focus on the title field
      const titleField = document.querySelector('#title');
      if (titleField) {
        titleField.focus();
      }
    });
  }

  populateMovieInfoSection (data) {
    const movieInfoContainer = document.getElementById('movie-info-container');
    const moviePoster = document.getElementById('movie-poster');
    const movieTitle = document.getElementById('movie-title');

    if (!movieInfoContainer || !moviePoster || !movieTitle) {
      return;
    }

    // Show the container
    movieInfoContainer.classList.remove('hidden');

    // Set the movie title
    movieTitle.textContent = data.title || 'Unknown Title';

    // Set the movie poster
    if (data.posterUrl) {
      moviePoster.src = data.posterUrl;
      moviePoster.style.display = 'block';
    } else {
      // Hide poster if no URL available
      moviePoster.style.display = 'none';
    }
  }

  setupImdbConfirmationButtons () {
    const yesButton = document.getElementById('imdb-confirm-yes');
    const noButton = document.getElementById('imdb-confirm-no');

    if (yesButton) {
      // Remove existing listeners to prevent duplicates
      if (this.boundHandleImdbConfirmYes) {
        yesButton.removeEventListener('click', this.boundHandleImdbConfirmYes);
      }
      this.boundHandleImdbConfirmYes = this.handleImdbConfirmYes.bind(this);
      yesButton.addEventListener('click', this.boundHandleImdbConfirmYes);
    }

    if (noButton) {
      // Remove existing listeners to prevent duplicates
      if (this.boundHandleImdbConfirmNo) {
        noButton.removeEventListener('click', this.boundHandleImdbConfirmNo);
      }
      this.boundHandleImdbConfirmNo = this.handleImdbConfirmNo.bind(this);
      noButton.addEventListener('click', this.boundHandleImdbConfirmNo);
    }
  }

  handleImdbConfirmYes () {
    console.log('IMDB confirmation: User clicked Yes');

    // Mark IMDB as confirmed
    this.imdbConfirmed = true;
    console.log('IMDB confirmed flag set to true');

    // Add loading state to button
    const yesButton = document.getElementById('imdb-confirm-yes');
    if (yesButton) {
      yesButton.disabled = true;
      yesButton.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Loading...';
    }

    // Update button states to reflect the confirmed status
    // This will re-validate and enable the Next button
    this.updateButtonStates();

    // Go to step 2 (next step)
    if (this.wizard) {
      this.wizard.goToStep(2);
    } else {
      this.showStep(2);
    }
  }

  handleImdbConfirmNo () {
    console.log('IMDB confirmation: User clicked No');

    // Clear the IMDB URL and hide confirmation
    const imdbLinkField = document.querySelector('#imdbLink');
    const confirmationSection = document.getElementById('imdb-confirmation');

    // Get the IMDB input group and heading elements to show them again
    const imdbInputGroup = imdbLinkField?.closest('.form-group');
    const imdbHeading = document.querySelector('.cell.medium-6 h3');
    const imdbSubheading = document.querySelector('.cell.medium-6 h5');

    if (imdbLinkField) {
      imdbLinkField.value = '';
      // Clear any validation states
      imdbLinkField.removeAttribute('data-touched');
      this.clearFieldError(imdbLinkField);
    }

    if (confirmationSection) {
      confirmationSection.classList.add('hidden');
    }

    // Show the IMDB input group again
    if (imdbInputGroup) {
      imdbInputGroup.classList.remove('hidden');
    }

    // Show the heading elements again
    if (imdbHeading) {
      imdbHeading.classList.remove('hidden');
    }
    if (imdbSubheading) {
      imdbSubheading.classList.remove('hidden');
    }

    // Clear stored IMDB data
    if (this.formData) {
      delete this.formData.imdb_id;
      delete this.formData.imdb_poster;
    }

    // Clear any populated form fields
    const titleField = document.querySelector('#title');
    const synopsisField = document.querySelector('#synopsis');

    if (titleField) {
      titleField.value = '';
    }
    if (synopsisField) {
      synopsisField.value = '';
    }

    // Focus back on the IMDB input field
    if (imdbLinkField) {
      imdbLinkField.focus();
    }

    this.showNotification('IMDB data cleared. Please enter a new IMDB URL.', 'info');
  }

  checkAndShowLinkFields () {
    // Check trailer link/password fields
    const trailerLink = document.getElementById('trailer_link');
    const trailerPassword = document.getElementById('trailer_password');
    const trailerContainer = document.getElementById('trailer-link-fields');
    const trailerButton = document.querySelector('[data-target="trailer-link-fields"]');

    if (trailerContainer && trailerButton && (trailerLink?.value || trailerPassword?.value)) {
      trailerContainer.classList.remove('hidden');
      trailerButton.setAttribute('aria-expanded', 'true');
      trailerButton.style.display = 'none'; // Hide button when fields are pre-filled
    }

    // Check feature link/password fields
    const featureLink = document.getElementById('feature_link');
    const featurePassword = document.getElementById('feature_password');
    const featureContainer = document.getElementById('feature-link-fields');
    const featureButton = document.querySelector('[data-target="feature-link-fields"]');

    if (featureContainer && featureButton && (featureLink?.value || featurePassword?.value)) {
      featureContainer.classList.remove('hidden');
      featureButton.setAttribute('aria-expanded', 'true');
      featureButton.style.display = 'none'; // Hide button when fields are pre-filled
    }
  }

  initialize () {
    console.log('SubmitTitle initialize called');
    console.log('window.sflow:', window.sflow);
    console.log('window.sflow.Wizard:', window.sflow?.Wizard);

    // Create step wizard using the new Wizard component
    this.createStepWizard();

    // Initialize genres multi-select
    this.initializeGenres();

    // Initialize first step
    this.showStep(1);

    // Set up button state management
    this.setupButtonStateManagement();

    // Ensure button states are correct after a brief delay
    // to allow DOM updates to complete
    setTimeout(() => {
      this.updateButtonStates();
    }, 100);

    // Apply prefill data if available
    if (window.submissionPrefillData && Object.keys(window.submissionPrefillData).length > 0) {
      this.applyPrefillData(window.submissionPrefillData);
    }

    // Handle field visibility based on form config
    if (window.submissionFormConfig) {
      this.applyFormConfig(window.submissionFormConfig);
    }

    // Initialize CAP if enabled
    if (window.capConfig && window.capConfig.enabled) {
      this.initializeCAP();
    }

    // Mark initialization complete (used to prevent auto-scroll on page load)
    this._initialized = true;
  }

  initializePlatformMultiselect () {
    const platformSelect = document.querySelector('#platform-select');
    if (!platformSelect) {
      console.error('Platform select not found');
      return;
    }

    // Initialize multiselect plugin
    $(platformSelect).multiselect({
      texts: {
        placeholder: 'Select one or more platforms',
        selectedOptions: ' platform(s) selected',
      },
      columns: 1,
      search: false,
      selectAll: false,
      onOptionClick: (element, option) => {
        // Handle "Other" option selection
        const otherInput = document.querySelector('input[name="desired_platform_other"]');
        if (option.value === 'other' && option.checked) {
          otherInput.classList.remove('hidden');
          otherInput.setAttribute('required', 'required');
        } else if (option.value === 'other' && !option.checked) {
          otherInput.classList.add('hidden');
          otherInput.removeAttribute('required');
          otherInput.value = '';
        }

        // Trigger change event for validation
        $(platformSelect).trigger('change');
      },
    });

    // Handle platform selection changes
    $(platformSelect).on('change', () => {
      // Clear any existing error messages
      const errorMsg = platformSelect.parentNode.querySelector('.field-error-message');
      if (errorMsg) {
        errorMsg.remove();
      }
    });
  }

  initializeGenres () {
    const container = document.querySelector('#genres-container');
    if (!container) {
      console.error('Genres container not found');
      return;
    }

    console.log('Initializing genres...');

    // Show loading state
    container.innerHTML = '<div class="text-muted"><i class="fas fa-spinner fa-spin"></i> Loading genres...</div>';

    // Fetch genres from API
    fetch('/api/public/genres')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch genres');
        }
        return response.json();
      })
      .then(response => {
        if (!response.success || !response.data) {
          throw new Error('Invalid response format');
        }

        // Clear loading state
        container.innerHTML = '';

        // Create native select element
        const selectElement = document.createElement('select');
        selectElement.multiple = true;
        selectElement.id = 'genres-select';
        selectElement.name = 'genres[]';
        selectElement.required = true;

        // Store genres data
        this.genresData = response.data;
        console.log('Genres loaded:', this.genresData.length, 'genres');

        // Create options
        response.data.forEach(genre => {
          const option = document.createElement('option');
          option.value = genre.id;
          option.textContent = genre.name;
          selectElement.appendChild(option);
        });

        // Add select to container
        container.appendChild(selectElement);

        // Initialize jQuery multiselect
        $(selectElement).multiselect({
          columns: 1,
          search: true,
          selectAll: true,
          texts: {
            placeholder: 'Click to select genres...',
            search: 'Search genres...',
            selectAll: 'Select all genres',
            unselectAll: 'Unselect all',
            selectedOptions: ' genres selected',
            noneSelected: 'No genres selected',
          },
          onOptionClick: (element, option) => {
            // Optional: Handle genre selection changes
            this.updateGenreCount();

            // Check if any genres are selected and update validation state
            const selectedGenres = $(selectElement).val() || [];
            const genresContainer = document.querySelector('#genres-container');
            if (genresContainer) {
              if (selectedGenres.length > 0) {
                this.clearMultiselectError(genresContainer);
              }
            }
          },
        });

        // Store reference to the multiselect wrapper
        this.genreMultiselect = container.querySelector('.ms-options-wrap');
      })
      .catch(error => {
        console.error('Failed to load genres:', error);
        container.innerHTML = '<div class="text-error">Failed to load genres. Please refresh the page.</div>';

        // Fallback to hardcoded genres
        const fallbackGenres = [
          'Action', 'Adventure', 'Animation', 'Comedy', 'Crime', 'Documentary',
          'Drama', 'Family', 'Fantasy', 'Horror', 'Musical', 'Mystery',
          'Romance', 'Sci-Fi', 'Sport', 'Thriller', 'War', 'Western',
        ];

        const checkboxGroup = document.createElement('div');
        checkboxGroup.className = 'checkbox-group no-checkmark inline';

        fallbackGenres.forEach(genre => {
          const genreId = `genre-${genre.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

          const checkbox = document.createElement('input');
          checkbox.type = 'checkbox';
          checkbox.id = genreId;
          checkbox.name = 'genres[]';
          checkbox.value = genre;

          const label = document.createElement('label');
          label.setAttribute('for', genreId);
          label.textContent = genre;

          checkboxGroup.appendChild(checkbox);
          checkboxGroup.appendChild(label);
        });

        container.appendChild(checkboxGroup);
      });
  }

  updateGenreCount () {
    // Optional: Update any UI elements that show the count of selected genres
    const selectedCount = $('#genres-select').val() ? $('#genres-select').val().length : 0;
    console.log(`${selectedCount} genres selected`);
  }

  createStepWizard () {
    console.log('createStepWizard called');
    console.log('Step wizard element:', this.el.stepWizard);

    if (!window.sflow?.Wizard) {
      console.error('Wizard component not loaded. window.sflow:', window.sflow);
      return;
    }

    const steps = [
      {
        label: 'Basic Information',
        validate: () => {
          // Don't force show errors during wizard's internal validation checks
          // Only show errors when user actually tries to proceed
          const result = this.validateStep(1, false);
          return result;
        },
      },
      {
        label: 'Content Information',
        validate: () => {
          const result = this.validateStep(2, false);
          return result;
        },
      },
      {
        label: 'Review Materials',
        validate: () => {
          const result = this.validateStep(3, false);
          return result;
        },
      },
      {
        label: 'Distribution Details',
        validate: () => {
          const result = this.validateStep(4, false);
          return result;
        },
      },
      {
        label: 'Contact Info',
        validate: () => {
          // When wizard validates for forward movement, we should show errors
          // Check if we're trying to move forward (to step 6)
          const movingForward = this.wizard && this.wizard.getCurrentStep() === 5;
          const result = this.validateStep(5, movingForward);
          return result;
        },
      },
      {
        label: 'Review & Submit',
        validate: () => true, // No validation needed for review
      },
    ];

    this.wizard = new window.sflow.Wizard({
      container: this.el.stepWizard,
      steps: steps,
      allowClickableSteps: true,
      validateOnStepChange: true,
      onStepChange: (data) => {
        // When moving forward, validation is already handled by the wizard
        // When moving backward, no validation needed
        this.showStep(data.to);

        // Store form data when leaving a step
        if (data.from < data.to) {
          this.saveStepData(data.from);
        }

        // Debug: Log completed steps and wizard state
        console.log('Step change:', data.from, '->', data.to);
        console.log('Completed steps:', [...this.wizard.completedSteps]);
        console.log('Current step:', this.wizard.getCurrentStep());

        // Update step classes to reflect completed state
        this.wizard.updateStepClasses();
      },
      onValidationError: (validation) => {
        // When validation fails during step change, force show the errors
        const currentStep = this.wizard.getCurrentStep();
        this.validateStep(currentStep, true);

        // Show notification about validation errors
        this.showNotification('Please fill in all required fields before proceeding.', 'error');

        // Focus on the first invalid field
        if (validation.fields && validation.fields.length > 0) {
          validation.fields[0].focus();
        }
      },
      onComplete: () => {
        // Don't auto-submit if we're just filling with dev data
        // if (!this.isDevFilling) {
        //   this.handleSubmit();
        // }
      },
    });

    // The wizard component already handles completed step navigation properly
    // We just need to ensure validation shows errors when needed

    // Listen to wizard events for additional functionality
    this.el.stepWizard.addEventListener('wizard:stepShown', (e) => {
      console.log('Wizard step shown:', e.detail.step);
    });
  }

  handleProductionPhaseChange (e) {
    const value = e.target.value;

    // Hide all notes using classes instead of inline styles
    if (this.el.postProductionNote) {
      this.el.postProductionNote.classList.toggle('hidden', value !== 'post_production');
    }
    if (this.el.otherProductionNote) {
      this.el.otherProductionNote.classList.toggle('hidden', value !== 'other');
    }

    // Check if "Other" is selected and show immediate error
    const phaseGroup = e.target.closest('.radio-group');
    if (phaseGroup) {
      const existingError = phaseGroup.parentNode.querySelector('.field-error-message');
    }
  }

  handleNext () {
    if (this.wizard) {
      // First check if current step is valid - force showing errors when clicking next
      const currentStep = this.wizard.getCurrentStep();
      const validation = this.validateStep(currentStep, true);

      if (!validation.valid) {
        // Show notification about validation errors
        this.showNotification('Please fill in all required fields before proceeding.', 'error');

        // Focus first invalid field if available
        if (validation.fields && validation.fields.length > 0) {
          validation.fields[0].focus();
        }

        return;
      }

      // The wizard now handles step completion tracking internally
      this.wizard.next();
    }
  }

  handlePrev () {
    if (this.wizard) {
      this.wizard.prev();
    }
  }

  handleForcePrev () {
    if (this.wizard) {
      // Temporarily disable validation
      const originalValidateOnStepChange = this.wizard.options.validateOnStepChange;
      this.wizard.options.validateOnStepChange = false;

      // Force the step change
      this.wizard.prev();

      // Restore validation setting
      this.wizard.options.validateOnStepChange = originalValidateOnStepChange;

      this.showNotification('Forced previous without validation (Development mode)', 'warning');
    }
  }

  handleForceNext () {
    if (this.wizard) {
      // Temporarily disable validation
      const originalValidateOnStepChange = this.wizard.options.validateOnStepChange;
      this.wizard.options.validateOnStepChange = false;

      // Force the step change
      this.wizard.next();

      // Restore validation setting
      this.wizard.options.validateOnStepChange = originalValidateOnStepChange;

      this.showNotification('Forced next without validation (Development mode)', 'warning');
    }
  }

  showStep (stepNumber) {
    console.log('showStep called with step:', stepNumber);

    // Re-query step cards in case they weren't available at initialization
    if (!this.el.stepCards || this.el.stepCards.length === 0) {
      this.el.stepCards = this.el.container.querySelectorAll('.step-card');
      console.log('Re-queried step cards:', this.el.stepCards.length);
    }

    // Hide all steps
    this.el.stepCards.forEach(card => {
      card.classList.add('hidden');
      card.classList.remove('active');
    });

    // Show current step
    const currentCard = this.el.container.querySelector(`.step-card[data-step="${stepNumber}"]`);
    if (currentCard) {
      currentCard.classList.remove('hidden');
      currentCard.classList.add('active');
    } else {
      console.error('Step card not found for step:', stepNumber);
    }

    // The wizard component handles its own step state management

    // Generate review content when showing final step
    if (stepNumber === 6) {
      this.generateReviewContent();
    }

    // Update current step reference for any legacy code
    this.currentStep = stepNumber;

    // Update button states for the new step
    this.updateButtonStates();

    // Scroll to top only if not initial load (user navigated to a different step)
    if (this._initialized) {
      this.el.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  validateStep (stepNumber, forceShowErrors = false) {
    // Use wizard's current step if no step number provided
    if (!stepNumber && this.wizard) {
      stepNumber = this.wizard.getCurrentStep();
    }

    // Get the current step card
    const stepCard = this.el.container.querySelector(`.step-card[data-step="${stepNumber}"]`);
    if (!stepCard) return { valid: true };

    const invalidFields = [];

    // Step 1 validation - check platforms, content type, and production phase
    if (stepNumber === 1) {
      // Check content type selection
      const featureTypeSelected = stepCard.querySelector('input[name="feature_type"]:checked');
      const featureTypeOther = stepCard.querySelector('#feature_type_other:checked');
      const featureTypeOtherInput = stepCard.querySelector('input[name="feature_type_other"]');

      if (!featureTypeSelected) {
        if (forceShowErrors) {
          const featureTypeGroup = stepCard.querySelector('.radio-group');
          if (featureTypeGroup && !featureTypeGroup.parentNode.querySelector('.field-error-message')) {
            const errorDiv = document.createElement('div');
            errorDiv.className = 'field-error-message';
            errorDiv.textContent = 'Please select a content type';
            errorDiv.setAttribute('role', 'alert');
            featureTypeGroup.parentNode.appendChild(errorDiv);
          }
        }
        invalidFields.push(stepCard.querySelector('input[name="feature_type"]'));
      }

      // If "Other" is selected for content type, validate the text input
      if (featureTypeOther && featureTypeOtherInput) {
        if (!featureTypeOtherInput.value.trim()) {
          if (forceShowErrors) {
            featureTypeOtherInput.setAttribute('data-touched', 'true');
            this.validateField(featureTypeOtherInput);
          }
          invalidFields.push(featureTypeOtherInput);
        }
      }

      // Check if at least one platform is selected (only if platform selection is visible)
      const platformSelectionGroup = stepCard.querySelector('#platform-selection-group');
      const platformSelect = stepCard.querySelector('#platform-select');
      const otherPlatformInput = stepCard.querySelector('input[name="desired_platform_other"]');

      // Only validate platforms if the selection group is visible
      if (platformSelectionGroup && platformSelectionGroup.style.display !== 'none' && platformSelect) {
        const selectedOptions = platformSelect.selectedOptions;
        if (selectedOptions.length === 0) {
          if (forceShowErrors) {
            // Show error message for platforms
            const platformContainer = platformSelect.parentNode;
            if (platformContainer && !platformContainer.querySelector('.field-error-message')) {
              const errorDiv = document.createElement('div');
              errorDiv.className = 'field-error-message';
              errorDiv.textContent = 'Please select at least one distribution platform';
              errorDiv.setAttribute('role', 'alert');
              platformContainer.appendChild(errorDiv);
            }
          }
          invalidFields.push(platformSelect);
        }

        // Check if "Other" is selected
        const otherSelected = Array.from(selectedOptions).some(opt => opt.value === 'other');
        if (otherSelected && otherPlatformInput && !otherPlatformInput.value.trim()) {
          if (forceShowErrors) {
            otherPlatformInput.setAttribute('data-touched', 'true');
            this.validateField(otherPlatformInput);
          }
          invalidFields.push(otherPlatformInput);
        }
      }

      // Check if "Other" is selected for production phase
      const selectedPhase = stepCard.querySelector('input[name="production_phase"]:checked');
      if (selectedPhase && selectedPhase.value === 'other') {
        invalidFields.push(selectedPhase);
      }

      if (invalidFields.length > 0) {
        if (forceShowErrors && invalidFields[0] && invalidFields[0].scrollIntoView) {
          invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return { valid: false, fields: invalidFields };
      }

      return { valid: true };
    }

    // Step 2 validation - check IMDB link, confirmation, and genres
    if (stepNumber === 2) {
      const imdbLink = stepCard.querySelector('#imdbLink');
      const confirmationSection = stepCard.querySelector('#imdb-confirmation');

      // If confirmation section is visible, check if user has confirmed
      if (confirmationSection && !confirmationSection.classList.contains('hidden')) {
        // Check if user has clicked "Yes" to confirm IMDB data
        if (!this.imdbConfirmed) {
          if (forceShowErrors) {
            this.showNotification('Please confirm the IMDB information by clicking "Yes" or "No" before proceeding.', 'error');
          }
          return { valid: false };
        }
        // User has confirmed, continue with other validations
      } else {
        // No confirmation shown yet, validate IMDB field
        if (!imdbLink || !imdbLink.value.trim()) {
          if (forceShowErrors) {
            if (imdbLink) {
              imdbLink.setAttribute('data-touched', 'true');
              this.validateField(imdbLink);
            }
          }
          if (imdbLink) {
            invalidFields.push(imdbLink);
          }
        } else if (!this.isValidImdbUrl(imdbLink.value.trim())) {
          if (forceShowErrors) {
            // Show custom error for invalid IMDB URL
            const existingError = imdbLink.parentNode.parentNode.querySelector('.field-error-message');
            if (existingError) {
              existingError.remove();
            }

            const errorDiv = document.createElement('div');
            errorDiv.className = 'field-error-message';
            errorDiv.textContent = 'Please enter a valid IMDB URL containing a title ID (e.g., https://www.imdb.com/title/tt1234567/ or https://pro.imdb.com/title/tt1234567/)';
            errorDiv.setAttribute('role', 'alert');
            imdbLink.parentNode.parentNode.appendChild(errorDiv);
          }
          invalidFields.push(imdbLink);
        }
      }

      // Check genres multi-select
      const genresSelect = stepCard.querySelector('#genres-select');
      const selectedGenres = genresSelect ? $(genresSelect).val() || [] : [];

      if (selectedGenres.length === 0) {
        const genresContainer = stepCard.querySelector('#genres-container');
        if (forceShowErrors) {
          // Mark the select as touched for validation
          if (genresSelect) {
            genresSelect.setAttribute('data-touched', 'true');
          }

          // Show error on the multiselect wrapper
          this.showMultiselectError(genresContainer, 'Please select at least one genre');
        }
        invalidFields.push(genresContainer);
      } else {
        // Clear error if genres are selected
        this.clearMultiselectError(stepCard.querySelector('#genres-container'));
      }

      if (invalidFields.length > 0) {
        if (forceShowErrors && invalidFields[0] && invalidFields[0].scrollIntoView) {
          invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return { valid: false, fields: invalidFields };
      }
    }

    // Note: IMDB link is now required, so it will be validated with other required fields

    // Find all required fields in the current step
    const requiredFields = stepCard.querySelectorAll('input[required], textarea[required], select[required]');

    requiredFields.forEach(field => {
      // Mark as touched if forcing errors
      if (forceShowErrors) {
        field.setAttribute('data-touched', 'true');
      }

      // Validate the field
      if (!this.validateField(field)) {
        invalidFields.push(field);
      }
    });

    // Check for file upload requirements
    if (stepNumber === 3) {
      // Trailer is now OPTIONAL - remove required attribute
      const trailerLink = stepCard.querySelector('#trailer_link');
      if (trailerLink) {
        trailerLink.removeAttribute('required');
        // Clear any validation errors on the field
        this.clearFieldError(trailerLink);
      }

      // Check content - either link or file is REQUIRED
      const featureLink = stepCard.querySelector('#feature_link');
      const featureLinkValue = featureLink?.value?.trim();
      const featureFile = (this.uploadedFiles?.feature_upload?.length > 0) ||
        stepCard.querySelector('input[name="feature_upload"]')?.files?.length > 0;

      // Update content link required attribute based on file status
      if (featureLink) {
        if (featureFile) {
          featureLink.removeAttribute('required');
          // Clear any validation errors on the field
          this.clearFieldError(featureLink);
        } else {
          featureLink.setAttribute('required', 'required');
        }
      }

      // Validate content - must have either link or file
      if (!featureLinkValue && !featureFile) {
        if (forceShowErrors && featureLink) {
          featureLink.setAttribute('data-touched', 'true');
          this.validateField(featureLink);
        }
        if (featureLink) {
          invalidFields.push(featureLink);
        }
      }
    }

    // Check for step 4 required fields
    if (stepNumber === 4) {
      // Check territories and other field
      const territoriesSelect = stepCard.querySelector('select[name="territories"]');
      const territoriesOtherInput = stepCard.querySelector('input[name="territories_other"]');

      if (territoriesSelect && territoriesSelect.value === 'other') {
        territoriesOtherInput.classList.remove('hidden');
        territoriesOtherInput.setAttribute('required', 'required');
        if (!territoriesOtherInput.value.trim()) {
          if (forceShowErrors) {
            territoriesOtherInput.setAttribute('data-touched', 'true');
            this.validateField(territoriesOtherInput);
          }
          invalidFields.push(territoriesOtherInput);
        }
      } else if (territoriesOtherInput) {
        territoriesOtherInput.classList.add('hidden');
        territoriesOtherInput.removeAttribute('required');
      }
    }

    // Check for step 6 required fields (agreement section)
    if (stepNumber === 6) {
      const agreementCheckbox = stepCard.querySelector('#distribution_agreement');
      const agreementFirstName = stepCard.querySelector('#agreement_first_name');
      const agreementLastName = stepCard.querySelector('#agreement_last_name');

      // Check if agreement checkbox is checked
      if (agreementCheckbox && !agreementCheckbox.checked) {
        if (forceShowErrors) {
          agreementCheckbox.setAttribute('data-touched', 'true');
          this.validateField(agreementCheckbox);
        }
        invalidFields.push(agreementCheckbox);
      }

      // Check first name
      if (agreementFirstName && !agreementFirstName.value.trim()) {
        if (forceShowErrors) {
          agreementFirstName.setAttribute('data-touched', 'true');
          this.validateField(agreementFirstName);
        }
        invalidFields.push(agreementFirstName);
      }

      // Check last name
      if (agreementLastName && !agreementLastName.value.trim()) {
        if (forceShowErrors) {
          agreementLastName.setAttribute('data-touched', 'true');
          this.validateField(agreementLastName);
        }
        invalidFields.push(agreementLastName);
      }
    }

    if (invalidFields.length > 0) {
      // Only scroll if we're forcing errors (user clicked next)
      if (forceShowErrors && invalidFields[0] && invalidFields[0].scrollIntoView) {
        invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return { valid: false, fields: invalidFields };
    }

    return { valid: true };
  }

  saveStepData (stepNumber) {
    const formData = new FormData(this.el.form);

    // Clear previous checkbox arrays for this step
    const stepCard = this.el.container.querySelector(`.step-card[data-step="${stepNumber}"]`);
    if (stepCard) {
      // Find all checkbox groups in this step
      const checkboxArrays = {};
      stepCard.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        if (checkbox.name.endsWith('[]')) {
          checkboxArrays[checkbox.name] = true;
        }
      });
      // Clear existing values for these checkbox arrays
      Object.keys(checkboxArrays).forEach(name => {
        this.formData[name] = [];
      });
    }

    // Save all form data for this step
    for (let [key, value] of formData.entries()) {
      if (key.endsWith('[]')) {
        // For array fields, collect all values
        if (!this.formData[key]) {
          this.formData[key] = [];
        }
        if (!this.formData[key].includes(value)) {
          this.formData[key].push(value);
        }
      } else {
        // For regular fields, just save the value
        this.formData[key] = value;
      }
    }

    // Handle genres multiselect on step 2
    if (stepNumber === 2) {
      const genresSelect = document.querySelector('#genres-select');
      if (genresSelect) {
        const selectedGenres = $(genresSelect).val() || [];
        this.formData['genres'] = selectedGenres;
        console.log('Saved genres to formData:', selectedGenres);
      }
    }
  }

  isValidImdbUrl (url) {
    // Check if URL contains imdb.com domain and an IMDB ID (tt followed by digits)
    return url.match(/imdb\.com.*tt\d+/i);
  }

  promptImdbPrefill () {
    const imdbUrl = this.el.imdbLink.value.trim();
    if (!imdbUrl || !this.isValidImdbUrl(imdbUrl)) {
      return;
    }

    // Mark that we've shown the prompt to avoid showing it multiple times
    this.imdbPromptShown = true;

    // Create a modal or inline prompt
    const promptContainer = document.createElement('div');
    promptContainer.className = 'imdb-prefill-prompt callout info has-icon';
    promptContainer.innerHTML = `
      <div class="callout-icon">
        <i class="fas fa-info-circle"></i>
      </div>
      <div class="callout-content">
        <p><strong>Would you like to automatically fill the form with data from IMDB?</strong></p>
        <p>This will populate the title, synopsis, genres, and other available information.</p>
        <div class="button-group mt-2">
          <button type="button" class="button primary small" id="imdb-prefill-yes">Yes, Prefill Data</button>
          <button type="button" class="button secondary small" id="imdb-prefill-no">No, I'll Enter Manually</button>
        </div>
      </div>
    `;

    // Insert after the IMDB input group
    const imdbGroup = this.el.imdbLink.closest('.form-group');
    imdbGroup.parentNode.insertBefore(promptContainer, imdbGroup.nextSibling);

    // Handle button clicks
    promptContainer.querySelector('#imdb-prefill-yes').addEventListener('click', () => {
      promptContainer.remove();
      this.fetchImdbData();
    });

    promptContainer.querySelector('#imdb-prefill-no').addEventListener('click', () => {
      promptContainer.remove();
      // Focus on the title field
      const titleField = document.querySelector('#title');
      if (titleField) {
        titleField.focus();
      }
    });
  }

  fetchImdbDataSilently () {
    const imdbUrl = this.el.imdbLink.value.trim();
    if (!imdbUrl || !this.isValidImdbUrl(imdbUrl) || this.isImdbFetching) {
      return;
    }

    // Set fetching state
    this.isImdbFetching = true;

    // Call the IMDB API
    fetch('/api/imdb?' + new URLSearchParams({
      imdb: imdbUrl,
      disableCache: 0, // Get fresh data
    }))
      .then(response => response.json())
      .then(response => {
        // Always reset button first
        this.el.fetchImdbBtn.disabled = false;
        this.el.fetchImdbBtn.innerHTML = 'Import Data';
        this.isImdbFetching = false;

        // Check if request was successful
        if (!response.success) {
          // Don't show error for auto-fetch, just log it
          console.log('Auto-fetch failed:', response.message);
          return;
        }

        const data = response.data;
        console.log('IMDB data fetched automatically:', data);

        // Show manual entry fields (same as manual fetch)
        if (this.el.manualEntryFields) {
          this.el.manualEntryFields.classList.remove('hidden');
        }

        // Populate form fields - overwrite existing data
        if (data.title) {
          const titleField = document.querySelector('#title');
          if (titleField) {
            titleField.value = data.title;
            titleField.setAttribute('data-touched', 'true');
            this.validateField(titleField);
          }
        }

        if (data.plot) {
          const synopsisField = document.querySelector('#synopsis');
          if (synopsisField) {
            synopsisField.value = data.plot;
            synopsisField.setAttribute('data-touched', 'true');
            this.validateField(synopsisField);
          }
        }

        // Handle genres if available
        if (data.genres && data.genres.length > 0) {
          const genresSelect = document.querySelector('#genres-select');
          if (genresSelect) {
            // Clear existing selections first
            $(genresSelect).multiselect('deselectAll', false);
            const imdbGenres = data.genres;
            const selectedValues = [];

            imdbGenres.forEach(imdbGenre => {
              const matchingGenre = this.genresData?.find(dbGenre => {
                const dbGenreName = dbGenre.name.toLowerCase();
                const imdbGenreName = imdbGenre.toLowerCase();

                // Direct match
                if (dbGenreName === imdbGenreName) return true;

                // Handle common variations
                if (dbGenreName === 'sci-fi' && (imdbGenreName === 'science fiction' || imdbGenreName === 'sci-fi')) return true;
                if (dbGenreName === 'action-comedy' && (imdbGenreName === 'action' || imdbGenreName === 'comedy')) return true;
                if (dbGenreName === 'romantic-comedy' && (imdbGenreName === 'romance' || imdbGenreName === 'comedy')) return true;
                if (dbGenreName === 'music' && imdbGenreName === 'musical') return true;
                if (dbGenreName === 'musical' && imdbGenreName === 'music') return true;

                return false;
              });

              if (matchingGenre) {
                selectedValues.push(matchingGenre.id.toString());
              }
            });

            // Select the matching genres
            if (selectedValues.length > 0) {
              setTimeout(() => {
                // Select genres
                selectedValues.forEach(value => {
                  $(genresSelect).find(`option[value="${value}"]`).prop('selected', true);
                });

                // Update multiselect
                $(genresSelect).multiselect('reload');

                // Clear the error state
                const genresContainer = document.querySelector('#genres-container');
                if (genresContainer) {
                  this.clearMultiselectError(genresContainer);
                }

                console.log('Auto-selected genres:', selectedValues);
              }, 100);
            }
          }
        }

        // Handle additional fields that might be available
        if (data.year) {
          const yearField = document.querySelector('#year');
          if (yearField) {
            yearField.value = data.year;
            yearField.setAttribute('data-touched', 'true');
            this.validateField(yearField);
          }
        }

        if (data.runtime) {
          const runtimeField = document.querySelector('#runtime');
          if (runtimeField) {
            runtimeField.value = data.runtime;
            runtimeField.setAttribute('data-touched', 'true');
            this.validateField(runtimeField);
          }
        }

        // Store IMDB ID for later use
        this.formData.imdb_id = data.imdb_id || data.id;

        // Blur the IMDB field to trigger validation and enable Next button
        if (this.el.imdbLink) {
          this.el.imdbLink.blur();
          // Also trigger a change event to ensure button state updates
          this.el.imdbLink.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Explicitly update button states to reflect the new validation status
        // Small delay to ensure all field validations have completed
        setTimeout(() => {
          this.updateButtonStates();
        }, 100);
      })
      .catch(error => {
        // Reset button and state
        this.el.fetchImdbBtn.disabled = false;
        this.el.fetchImdbBtn.innerHTML = 'Import Data';
        this.isImdbFetching = false;

        console.error('IMDB auto-fetch error:', error);
        // Don't show error notifications for auto-fetch
      });
  }

  fetchImdbData () {
    const imdbUrl = this.el.imdbLink.value.trim();
    if (!imdbUrl) {
      this.showError('Please enter an IMDB link.');
      return;
    }

    // Show loading state
    this.el.fetchImdbBtn.disabled = true;
    this.el.fetchImdbBtn.innerHTML = '&nbsp;&nbsp;Fetching&nbsp;&nbsp;';

    // Call the IMDB API
    fetch('/api/imdb?' + new URLSearchParams({
      imdb: imdbUrl,
      disableCache: 0, // Get fresh data
    }))
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch IMDB data');
        }
        return response.json();
      })
      .then(response => {
        // Reset button
        this.el.fetchImdbBtn.disabled = false;
        this.el.fetchImdbBtn.innerHTML = 'Fetch';

        // Check if request was successful
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch IMDB data');
        }

        const data = response.data;

        // Show manual entry fields
        this.el.manualEntryFields.classList.remove('hidden');

        // Also slide up the "or" text and button if they're still visible
        const orText = this.el.manualEntryToggle?.parentElement?.previousElementSibling;
        const buttonContainer = this.el.manualEntryToggle?.parentElement;

        if (orText && orText.textContent.trim() === 'or' && !orText.classList.contains('slide-up')) {
          orText.classList.add('slide-up');
        }
        if (buttonContainer && !buttonContainer.classList.contains('slide-up')) {
          buttonContainer.classList.add('slide-up');
        }

        // Populate fields with IMDB data
        if (data.title) {
          const titleField = document.querySelector('#title');
          titleField.value = data.title;
          titleField.setAttribute('data-touched', 'true');
          this.validateField(titleField);
        }

        if (data.plot) {
          const synopsisField = document.querySelector('#synopsis');
          synopsisField.value = data.plot;
          synopsisField.setAttribute('data-touched', 'true');
          this.validateField(synopsisField);
        }

        // Note: Content type is now determined by feature_type in Step 1
        // We could update feature_type based on IMDB data if needed

        // Handle genres with jQuery multiselect
        console.log('IMDB data genres:', data.genres || data.genre);
        console.log('genresData available:', !!this.genresData);

        if ((data.genres || data.genre) && this.genresData) {
          const genresSelect = document.querySelector('#genres-select');
          console.log('Genres select element found:', !!genresSelect);

          if (genresSelect) {
            // Clear existing selections
            $(genresSelect).multiselect('deselectAll', false);

            // Use 'genres' field first (from imdb.json), fallback to 'genre'
            let imdbGenres = data.genres || data.genre || [];

            // Ensure it's an array
            if (!Array.isArray(imdbGenres)) {
              imdbGenres = [imdbGenres];
            }

            const selectedValues = [];

            imdbGenres.forEach(imdbGenre => {
              // Find matching genre in our database
              const matchingGenre = this.genresData.find(dbGenre => {
                const dbGenreName = dbGenre.name.toLowerCase();
                const imdbGenreName = imdbGenre.toLowerCase();

                // Direct match
                if (dbGenreName === imdbGenreName) return true;

                // Handle common variations
                if (dbGenreName === 'sci-fi' && (imdbGenreName === 'science fiction' || imdbGenreName === 'sci-fi')) return true;
                if (dbGenreName === 'science-fi' && imdbGenreName === 'sci-fi') return true;
                if (dbGenreName === 'action-comedy' && (imdbGenreName === 'action' || imdbGenreName === 'comedy')) return true;
                if (dbGenreName === 'romantic-comedy' && (imdbGenreName === 'romance' || imdbGenreName === 'comedy')) return true;
                if (dbGenreName === 'music' && imdbGenreName === 'musical') return true;
                if (dbGenreName === 'musical' && imdbGenreName === 'music') return true;

                // Don't match too broadly
                if (dbGenreName === 'comedy' && imdbGenreName === 'action') return false;
                if (dbGenreName === 'action' && imdbGenreName === 'comedy') return false;

                // Partial match only for compound genres
                if (dbGenreName.includes('-')) {
                  return dbGenreName.includes(imdbGenreName);
                }

                return false;
              });

              if (matchingGenre) {
                selectedValues.push(matchingGenre.id.toString());
              }
            });

            // Log genre matching for debugging
            console.log('IMDB genres:', imdbGenres);
            console.log('Matched genre IDs:', selectedValues);

            // Select the matching genres
            if (selectedValues.length > 0) {
              console.log('Selecting genre values:', selectedValues);

              // Check if multiselect is initialized
              const msOptions = $(genresSelect).next('.ms-options-wrap');
              console.log('Multiselect wrapper found:', msOptions.length > 0);

              // Also update the multiselect styling to match theme
              if (msOptions.length > 0) {
                msOptions.addClass('theme-multiselect');
              }

              // Use a small delay to ensure multiselect is ready
              setTimeout(() => {
                // First, make sure the select element has the values selected
                selectedValues.forEach(value => {
                  $(genresSelect).find(`option[value="${value}"]`).prop('selected', true);
                });

                // Then update multiselect
                $(genresSelect).multiselect('reload');

                // Also try the refresh method
                $(genresSelect).multiselect('refresh');

                // Verify selection worked
                const actualSelected = $(genresSelect).val() || [];
                console.log('Actually selected after refresh:', actualSelected);

                // Clear the error state and add valid state if genres were auto-selected
                const genresContainer = document.querySelector('#genres-container');
                if (genresContainer && actualSelected.length > 0) {
                  // Use the clearMultiselectError method to add the is-valid class
                  this.clearMultiselectError(genresContainer);
                }

                this.showInfo(`${selectedValues.length} genres auto-selected from IMDB`);
              }, 100);
            } else if (imdbGenres.length > 0) {
              console.log('No matching genres found for:', imdbGenres);
              this.showInfo('No matching genres found. Please select genres manually.');
            }
          }
        }

        // Handle additional fields that might be available
        if (data.year) {
          const yearField = document.querySelector('#year');
          if (yearField) {
            yearField.value = data.year;
          }
        }

        if (data.runtime) {
          const runtimeField = document.querySelector('#runtime');
          if (runtimeField) {
            runtimeField.value = data.runtime;
          }
        }

        // Store IMDB ID for later use
        this.formData.imdb_id = data.imdb_id || data.id;

        this.showSuccess('IMDB data imported successfully!');

        // Blur the IMDB field to trigger validation and enable Next button
        if (this.el.imdbLink) {
          this.el.imdbLink.blur();
          // Also trigger a change event to ensure button state updates
          this.el.imdbLink.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Explicitly update button states to reflect the new validation status
        // Small delay to ensure all field validations have completed
        setTimeout(() => {
          this.updateButtonStates();
        }, 100);
      })
      .catch(error => {
        // Reset button
        this.el.fetchImdbBtn.disabled = false;
        this.el.fetchImdbBtn.innerHTML = 'Fetch';

        console.error('IMDB fetch error:', error);

        // Check if it's a rate limit error
        if (error.message && error.message.includes('Rate limit')) {
          this.showError('Too many requests. Please try again later.');
        } else {
          this.showError('Failed to fetch IMDB data. Please enter information manually.');
        }
      });
  }

  handleFileSelect (input, area) {
    // This method is now a no-op since FileUpload component handles everything
    // We keep it for backward compatibility
    console.log('File selected - FileUpload component handles the UI');
  }

  generateReviewContent () {
    // Save current step data before generating review
    this.saveStepData(5);

    const reviewContainer = document.querySelector('#final-review-content');
    if (!reviewContainer) return;

    // Gather all form data
    const formData = new FormData(this.el.form);
    const data = {};
    for (let [key, value] of formData.entries()) {
      if (!data[key]) {
        data[key] = value;
      } else if (Array.isArray(data[key])) {
        data[key].push(value);
      } else {
        data[key] = [data[key], value];
      }
    }

    // Build review HTML
    let reviewHTML = `
      <div class="review-sections">
        <!-- Basic Information -->
        <div class="review-section">
          <h4>Basic Information <a href="#" class="edit-link" data-step="1"><i class="fas fa-edit"></i> Edit</a></h4>
          <p><strong>Content Type:</strong> ${this.getFeatureTypeLabel(data.feature_type, data.feature_type_other)}</p>
          <p><strong>Production Phase:</strong> ${this.getProductionPhaseLabel(data.production_phase)}</p>
          <p><strong>Desired Platforms:</strong> ${this.getSelectedPlatforms()}</p>
        </div>
        
        <!-- Content Information -->
        <div class="review-section">
          <h4>Content Information <a href="#" class="edit-link" data-step="2"><i class="fas fa-edit"></i> Edit</a></h4>
          <p><strong>Title:</strong> ${data.title || 'Not provided'}</p>
          <p><strong>Synopsis:</strong> ${data.synopsis || 'Not provided'}</p>
          <p><strong>Genres:</strong> ${this.getSelectedGenres()}</p>
          ${data.festivals ? `<p><strong>Festivals:</strong> ${data.festivals}</p>` : ''}
          ${data.awards ? `<p><strong>Awards:</strong> ${data.awards}</p>` : ''}
        </div>
        
        <!-- Review Materials -->
        <div class="review-section">
          <h4>Review Materials <a href="#" class="edit-link" data-step="3"><i class="fas fa-edit"></i> Edit</a></h4>
          ${data.trailer_link ? `<p><strong>Trailer Link:</strong> ${data.trailer_link}</p>` : ''}
          ${data.feature_link ? `<p><strong>Content Link:</strong> ${data.feature_link}</p>` : ''}
          ${this.uploadedFiles && this.uploadedFiles.trailer_upload ? `<p><strong>Trailer Upload:</strong> File uploaded</p>` : ''}
          ${this.uploadedFiles && this.uploadedFiles.feature_upload ? `<p><strong>Content Upload:</strong> File uploaded</p>` : ''}
          ${data.notes ? `<p><strong>Notes:</strong> ${data.notes}</p>` : ''}
        </div>
        
        <!-- Distribution -->
        <div class="review-section">
          <h4>Distribution Details <a href="#" class="edit-link" data-step="4"><i class="fas fa-edit"></i> Edit</a></h4>
          <p><strong>Territories:</strong> ${this.getTerritoryLabel(data.territories, data.territories_other)}</p>
          <p><strong>Available Rights:</strong> ${this.getRightsLabel(data.available_rights, data.rights_some_details)}</p>
          ${data.marketing_info ? `<p><strong>Marketing Info:</strong> ${data.marketing_info}</p>` : ''}
        </div>
        
        <!-- Contact Info -->
        <div class="review-section">
          <h4>Contact Information <a href="#" class="edit-link" data-step="5"><i class="fas fa-edit"></i> Edit</a></h4>
          <p><strong>Name:</strong> ${data.first_name} ${data.last_name}</p>
          ${data.role ? `<p><strong>Title/Role:</strong> ${data.role}</p>` : ''}
          <p><strong>Email:</strong> ${data.email}</p>
          ${data.phone_country_code || data.phone_number ? `<p><strong>Phone:</strong> ${data.phone_country_code || ''} ${data.phone_number || ''}</p>` : ''}
        </div>
      </div>
    `;

    reviewContainer.innerHTML = reviewHTML;

    // Add event listeners to edit links
    reviewContainer.querySelectorAll('.edit-link').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetStep = parseInt(link.dataset.step);
        this.wizard.goToStep(targetStep);
      });
    });

    // Populate agreement section
    this.populateAgreementSection(data);
  }

  populateAgreementSection (data) {
    const agreementSection = document.getElementById('agreement-section');
    const platformAgreements = document.getElementById('platform-agreements');

    if (!agreementSection || !platformAgreements) return;

    // Get selected platforms - only include platforms with known agreements
    // Custom "other" platforms are excluded since they don't have distribution agreements
    const platformsWithAgreements = [];
    const platformSelect = document.querySelector('#platform-select');

    // Map platform values to proper names and terms URLs
    const platformConfig = {
      'bloodstream': { name: 'BloodStream', termsUrl: '/terms?type=bloodstream' },
      'prideflix': { name: 'PrideFlix', termsUrl: '/terms?type=prideflix' },
      'youtube': { name: 'YouTube Network', termsUrl: '/terms?type=youtube' },
    };

    if (platformSelect) {
      Array.from(platformSelect.selectedOptions).forEach(option => {
        // Only include platforms that have known agreements (skip 'other')
        if (platformConfig[option.value]) {
          platformsWithAgreements.push(platformConfig[option.value]);
        }
      });
    }

    // If no platforms with agreements selected, hide the agreement section
    if (platformsWithAgreements.length === 0) {
      agreementSection.classList.add('hidden');
      return;
    }

    // Create links for each platform with proper terms URLs
    const platformLinks = platformsWithAgreements.map(platform => {
      return `<a href="${platform.termsUrl}" class="platform-agreement-link" data-platform="${platform.name}" target="_blank">${platform.name}</a>`;
    }).join(', ');

    // Update the agreement text
    platformAgreements.innerHTML = platformLinks;

    // Show the agreement section
    agreementSection.classList.remove('hidden');

    // Add event listeners to agreement fields to trigger validation and button state update
    const agreementCheckbox = document.getElementById('distribution_agreement');
    const agreementFirstName = document.getElementById('agreement_first_name');
    const agreementLastName = document.getElementById('agreement_last_name');

    // Add blur event listeners to trigger validation when user exits a field
    [agreementCheckbox, agreementFirstName, agreementLastName].forEach(field => {
      if (field) {
        // Mark as touched and validate on blur (when user exits field)
        field.addEventListener('blur', () => {
          field.setAttribute('data-touched', 'true');
          this.validateField(field);
          this.updateButtonStates();
        });

        // Update button state on change/input but don't trigger validation
        field.addEventListener('change', () => this.updateButtonStates());
        field.addEventListener('input', () => this.updateButtonStates());
      }
    });

    // DON'T trigger initial validation for step 6 - let user interact first
    // Just update button visual state without validating
    if (this.wizard && this.wizard.getCurrentStep() === 6) {
      // Update button state without triggering validation
      const submitButton = document.querySelector('.step-card[data-step="6"] button[type="submit"]');
      if (submitButton) {
        // Just add disabled visual state initially
        submitButton.classList.add('disabled');
        submitButton.setAttribute('aria-disabled', 'true');
      }
    }
  }

  getFeatureTypeLabel (value, other) {
    const labels = {
      'tv_show': 'Series',
      'movie': 'Movie',
      'short_film': 'Short Content',
      'other': other || 'Other',
    };
    return labels[value] || value || 'Not specified';
  }

  getProductionPhaseLabel (value) {
    const labels = {
      'distribution': 'Delivery / Distribution',
      'post_production': 'Post-Production',
      'other': 'Other',
    };
    return labels[value] || value || 'Not specified';
  }

  getSelectedGenres () {
    const genresSelect = document.querySelector('#genres-select');
    if (!genresSelect || !this.genresData) {
      return 'None selected';
    }

    const selectedValues = $(genresSelect).val() || [];
    const selectedNames = selectedValues.map(value => {
      const genre = this.genresData.find(g => g.id.toString() === value);
      return genre ? genre.name : value;
    });

    return selectedNames.length > 0 ? selectedNames.join(', ') : 'None selected';
  }

  getTerritoryLabel (territory, other) {
    const labels = {
      'worldwide': 'Worldwide',
      'english_speaking': 'All English Speaking',
      'north_america': 'North America',
      'us_only': 'US Only',
      'other': other || 'Other',
    };
    return labels[territory] || territory || 'Not specified';
  }

  getRightsLabel (rights, details) {
    if (rights === 'all') {
      return 'All rights are available for use exclusively';
    } else if (rights === 'some' && details) {
      return `Some rights/platforms: ${details}`;
    }
    return 'Not specified';
  }

  getSelectedPlatforms () {
    const selected = [];
    const platformSelect = document.querySelector('#platform-select');
    if (platformSelect) {
      Array.from(platformSelect.selectedOptions).forEach(option => {
        if (option.value === 'other') {
          const otherValue = document.querySelector('input[name="desired_platform_other"]')?.value;
          if (otherValue) {
            selected.push(otherValue);
          } else {
            selected.push('Other');
          }
        } else {
          selected.push(option.textContent);
        }
      });
    }
    return selected.length > 0 ? selected.join(', ') : 'None selected';
  }

  handleSubmit (e) {
    // Handle both event-based calls and direct calls
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Validate step 6 with force show errors (same as clicking Next button)
    const validation = this.validateStep(6, true);

    if (!validation.valid) {
      // Show notification about validation errors
      this.showNotification('Please fill in all required fields before submitting.', 'error');

      // Focus on the first invalid field
      if (validation.fields && validation.fields[0]) {
        const firstInvalid = validation.fields[0];
        if (firstInvalid.focus) {
          firstInvalid.focus();
        }
      }
      return;
    }

    // Check CAP verification if enabled
    if (window.capConfig && window.capConfig.enabled) {
      if (!this.capSolver || !this.capSolver.isSolved()) {
        this.showNotification('Verification required. Please complete the security challenge.', 'error');

        // Scroll to CAP status section
        const capStatus = document.getElementById('cap-status');
        if (capStatus) {
          capStatus.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return;
      }
    }

    // Save final step data
    this.saveStepData(5);

    // Show loading state
    const submitBtn = this.el.container.querySelector('button[type="submit"]');
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Submitting...';

    // Continue with submission
    this.submitForm(submitBtn, originalText);
  }

  submitForm (submitBtn, originalText) {
    // Prepare submission data
    const submissionData = this.prepareSubmissionData();

    // Submit to API
    fetch('/api/public/titles/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(submissionData),
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => Promise.reject(err));
        }
        return response.json();
      })
      .then(response => {
        // Reset button
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;

        if (response.success) {
          // Show thank you message
          this.showThankYouMessage();

          // Scroll to top
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.showError(response.message || 'Failed to submit. Please try again.');
        }
      })
      .catch(error => {
        // Reset button
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;

        console.error('Submission error:', error);
        // Show error message
        this.showError(error.message || 'An error occurred. Please try again later.');
      });
  }

  prepareSubmissionData () {
    console.log('=== PREPARING SUBMISSION DATA ===');
    console.log('Current uploadedFiles state:', this.uploadedFiles);
    console.log('uploadedFiles keys:', this.uploadedFiles ? Object.keys(this.uploadedFiles) : 'undefined');

    // Combine all form data
    const formData = new FormData(this.el.form);
    const data = {};

    // Convert FormData to object
    for (let [key, value] of formData.entries()) {
      if (key.endsWith('[]')) {
        // Handle array fields
        const cleanKey = key.slice(0, -2);
        if (!data[cleanKey]) {
          data[cleanKey] = [];
        }
        data[cleanKey].push(value);
      } else {
        data[key] = value;
      }
    }

    // Add any stored data that might not be in the current form
    Object.assign(data, this.formData);

    // Add uploaded file references
    if (this.uploadedFiles) {
      console.log('Adding uploaded files to submission:', this.uploadedFiles);
      for (const [key, files] of Object.entries(this.uploadedFiles)) {
        // Ensure we're sending the file data correctly
        if (Array.isArray(files) && files.length > 0) {
          data[key + '_uploaded'] = files;
          console.log(`Added ${key}_uploaded:`, files);
          // Log individual file details for debugging
          files.forEach((file, index) => {
            console.log(`  File ${index}:`, {
              file_id: file?.file_id,
              file_name: file?.file_name,
              gcs_url: file?.gcs_url,
            });
          });
        }
      }
    } else {
      console.log('No uploaded files found in this.uploadedFiles');
    }

    // Ensure genres is an array
    if (data.genres && !Array.isArray(data.genres)) {
      data.genres = [data.genres];
    }

    // Ensure desired_platform is an array
    if (data.desired_platform && !Array.isArray(data.desired_platform)) {
      data.desired_platform = [data.desired_platform];
    }

    // Add CAP challenge and solution if enabled
    if (window.capConfig && window.capConfig.enabled && this.capSolver) {
      data.cap_challenge = window.capConfig.challenge;
      data.cap_solution = this.capSolver.getSolution();
      console.log('Added CAP challenge and solution to submission');
    }

    return data;
  }

  showError (message) {
    this.showNotification(message, 'error');
  }

  showSuccess (message) {
    this.showNotification(message, 'success');
  }

  showThankYouMessage () {
    // Replace the entire form container with a thank you message
    const container = this.el.container;
    if (!container) return;

    // Create thank you content
    const thankYouHTML = `
      <div class="thank-you-container" style="text-align: center; padding: 60px 20px;">
        <div class="thank-you-icon" style="margin-bottom: 30px;">
          <i class="fas fa-check-circle" style="font-size: 80px; color: #28a745;"></i>
        </div>
        <h1 style="margin-bottom: 20px;">Thank You!</h1>
        <p style="font-size: 18px; margin-bottom: 30px;">Your content has been successfully submitted for review.</p>
        <p style="margin-bottom: 40px;">Our team will review your submission and contact you.</p>
        <div class="button-group" style="margin-top: 40px;">
          <a href="/" class="button secondary">Return to Home</a>
          <a href="/submit-title" class="button primary">Submit Another Title</a>
        </div>
      </div>
    `;

    // Replace container content
    container.innerHTML = thankYouHTML;

    // Add fade-in animation
    container.style.opacity = '0';
    container.style.transition = 'opacity 0.5s ease-in-out';
    setTimeout(() => {
      container.style.opacity = '1';
    }, 100);
  }

  showInfo (message) {
    this.showNotification(message, 'info');
  }

  showFieldError (field, message) {
    // Delegate to validator if it exists
    if (this.validator) {
      this.validator.showFieldError(field, message);
    }
  }

  clearFieldError (field) {
    // Delegate to validator if it exists
    if (this.validator) {
      this.validator.clearFieldError(field);
      // Show valid state if field has value
      if (field.value && field.checkValidity && field.checkValidity()) {
        this.showFieldValid(field);
      }
    }
  }

  showFieldValid (field) {
    // Delegate to validator if it exists
    if (this.validator) {
      // Skip validation styling for radio buttons and checkboxes
      if (field.type === 'radio' || field.type === 'checkbox') {
        return;
      }
      this.validator.showFieldValid(field);
    }
  }

  clearFieldValid (field) {
    field.classList.remove('is-valid');
  }

  showMultiselectError (container, message) {
    // Find the multiselect wrapper
    const msWrapper = container.querySelector('.ms-options-wrap');
    if (msWrapper) {
      // Add error class to the wrapper
      msWrapper.classList.add('is-invalid');

      // Find the button inside the wrapper
      const msButton = msWrapper.querySelector('button');
      if (msButton) {
        msButton.classList.add('is-invalid');
        msButton.setAttribute('aria-invalid', 'true');
      }
    }

    // Remove any existing error message
    const existingError = container.querySelector('.field-error-message');
    if (existingError) {
      existingError.remove();
    }

    // Add error message below the multiselect
    const errorDiv = document.createElement('div');
    errorDiv.className = 'field-error-message';
    errorDiv.textContent = message;
    errorDiv.setAttribute('role', 'alert');
    container.appendChild(errorDiv);
  }

  clearMultiselectError (container) {
    if (!container) return;

    // Find the multiselect wrapper
    const msWrapper = container.querySelector('.ms-options-wrap');
    if (msWrapper) {
      // Remove error class from wrapper
      msWrapper.classList.remove('is-invalid');
      msWrapper.classList.add('is-valid');

      // Find the button and update it
      const msButton = msWrapper.querySelector('button');
      if (msButton) {
        msButton.classList.remove('is-invalid');
        msButton.classList.add('is-valid');
        msButton.setAttribute('aria-invalid', 'false');
      }
    }

    // Remove error message
    const errorMessage = container.querySelector('.field-error-message');
    if (errorMessage) {
      errorMessage.remove();
    }
  }

  setupButtonStateManagement () {
    // Set up listeners for all form fields to update button states
    this.el.container.addEventListener('input', (e) => {
      this.updateButtonStates();
    });

    this.el.container.addEventListener('change', (e) => {
      this.updateButtonStates();
    });

    // Initial button state
    this.updateButtonStates();
  }

  updateButtonStates () {
    const currentStep = this.wizard ? this.wizard.getCurrentStep() : 1;
    const stepCard = this.el.container.querySelector(`.step-card[data-step="${currentStep}"]`);

    if (!stepCard) return;

    // Find the next button for current step
    const nextButton = stepCard.querySelector('.button-next:not(.button-force-next)');

    // Also check for submit button on step 6
    const submitButton = currentStep === 6 ? stepCard.querySelector('button[type="submit"]') : null;
    const buttonToUpdate = nextButton || submitButton;

    if (!buttonToUpdate) return;

    // Check if current step is valid without showing errors
    const isValid = this.isStepValid(currentStep);

    // Update button visual state only - don't use disabled attribute
    // so clicks can still trigger validation
    if (!isValid) {
      buttonToUpdate.classList.add('disabled');
      buttonToUpdate.setAttribute('aria-disabled', 'true');
    } else {
      buttonToUpdate.classList.remove('disabled');
      buttonToUpdate.setAttribute('aria-disabled', 'false');
    }
  }

  isStepValid (stepNumber) {
    // Use the validateStep method without showing errors
    const validation = this.validateStep(stepNumber, false);
    return validation.valid;
  }

  showNotification (message, type = 'info') {
    // Use the new StudioDome notification API if available
    if (window.StudioDome && window.StudioDome.notifications) {
      // Map error type to proper notification type
      const notificationType = type === 'error' ? 'error' : type;

      // Use the notification API with proper method names
      const methods = {
        'success': 'success',
        'error': 'error',
        'warning': 'warning',
        'info': 'info',
      };

      const method = methods[notificationType] || 'info';

      if (window.StudioDome.notifications[method]) {
        window.StudioDome.notifications[method](message, {
          duration: type === 'error' ? 8000 : 5000, // Errors stay longer (8s), others 5s
          position: 'top-right',
          closable: true, // Allow manual dismissal
          animate: true, // Enable animations
        });
      } else {
        // Fallback to console if method doesn't exist
        console[type === 'error' ? 'error' : 'log'](message);
      }
    } else {
      // Fallback to inline notification for older browsers or if API not loaded
      console.warn('StudioDome notification API not available, using fallback');

      // Map notification types to callout types
      const calloutType = type === 'error' ? 'alert' : type;

      // Create callout element using UI kit structure
      const callout = document.createElement('div');
      callout.className = `callout ${calloutType} has-icon animated`;
      callout.innerHTML = `
        <button class="callout-close">
          <i class="fas fa-times"></i>
        </button>
        <div class="callout-icon">
          <i class="fas fa-${type === 'error' ? 'exclamation-circle' : type === 'success' ? 'check-circle' : 'info-circle'}"></i>
        </div>
        <div class="callout-content">
          <p>${message}</p>
        </div>
      `;

      // Add close functionality
      const closeBtn = callout.querySelector('.callout-close');
      closeBtn.addEventListener('click', () => {
        callout.style.opacity = '0';
        callout.style.transform = 'translateY(-10px)';
        setTimeout(() => callout.remove(), 300);
      });

      // Add to notifications container at top of form
      let container = document.querySelector('.submit-title-notifications');
      if (!container) {
        container = document.createElement('div');
        container.className = 'submit-title-notifications';
        container.style.marginBottom = '1rem';
        // Insert at the top of the form
        this.el.form.insertBefore(container, this.el.form.firstChild);
      }

      // Clear existing notifications in the container
      container.innerHTML = '';
      container.appendChild(callout);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        callout.style.opacity = '0';
        callout.style.transform = 'translateY(-10px)';
        setTimeout(() => callout.remove(), 300);
      }, type === 'error' ? 8000 : 5000);
    }
  }

  clearFieldError (field) {
    if (!field) return;

    // Delegate to validator if it exists
    if (this.validator && this.validator.clearFieldError) {
      this.validator.clearFieldError(field);
      return;
    }

    // Fallback implementation
    field.classList.remove('is-invalid');
    field.classList.add('is-valid');
  }

  updateLinkRequirements () {
    // Get step 3 card
    const stepCard = this.el.container.querySelector('.step-card[data-step="3"]');
    if (!stepCard) return;

    // TRAILER IS NOW ALWAYS OPTIONAL
    const trailerLink = stepCard.querySelector('#trailer_link');
    if (trailerLink) {
      // Always keep trailer as optional
      trailerLink.removeAttribute('required');
      trailerLink.placeholder = 'Optional - add trailer link if available';
      // Clear any validation errors
      this.clearFieldError(trailerLink);
      // Remove is-invalid class if present
      trailerLink.classList.remove('is-invalid');
      // Update visual state to show it's optional
      const label = stepCard.querySelector('label[for="trailer_link"]');
      if (label) {
        const requiredSpan = label.querySelector('.required');
        if (requiredSpan) {
          requiredSpan.style.display = 'none';
        }
      }
    }

    // Check content file status
    const featureFile = (this.uploadedFiles?.feature_upload?.length > 0) ||
      stepCard.querySelector('input[name="feature_upload"]')?.files?.length > 0;
    const featureLink = stepCard.querySelector('#feature_link');

    if (featureLink) {
      if (featureFile) {
        // File is uploaded, link becomes optional
        featureLink.removeAttribute('required');
        featureLink.placeholder = 'Optional - file already uploaded';
        // Clear any validation errors
        this.clearFieldError(featureLink);
        // Remove is-invalid class if present
        featureLink.classList.remove('is-invalid');
        // Update visual state to show it's optional
        const label = stepCard.querySelector('label[for="feature_link"]');
        if (label) {
          const requiredSpan = label.querySelector('.required');
          if (requiredSpan) {
            requiredSpan.style.display = 'none';
          }
        }
      } else {
        // No file, link is required
        featureLink.setAttribute('required', 'required');
        featureLink.placeholder = 'https://vimeo.com/987654321';
        // Reset validation state when field becomes required again
        featureLink.classList.remove('is-valid');
        // If field is empty, it should not show as invalid until user interacts
        if (!featureLink.value) {
          featureLink.classList.remove('is-invalid');
          featureLink.removeAttribute('data-touched');
        }
        const label = stepCard.querySelector('label[for="feature_link"]');
        if (label) {
          const requiredSpan = label.querySelector('.required');
          if (requiredSpan) {
            requiredSpan.style.display = '';
          }
        }
      }
    }

    // Update button states since requirements may have changed
    this.updateButtonStates();
  }

  fillWithFakeData () {
    this.showNotification('Filling form with fake data...', 'info');

    // Set a flag to prevent auto-submission
    this.isDevFilling = true;

    // Step 1 - Content Type
    // Select movie type
    const movieRadio = this.el.container.querySelector('input[name="feature_type"][value="movie"]');
    if (movieRadio) {
      movieRadio.checked = true;
      movieRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Step 1 - Production Phase (already has default)
    // Select distribution phase
    const distributionRadio = this.el.container.querySelector('input[name="production_phase"][value="distribution"]');
    if (distributionRadio) {
      distributionRadio.checked = true;
      distributionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Step 1 - Select some platforms
    const platformSelect = this.el.container.querySelector('#platform-select');
    if (platformSelect) {
      const platformValues = ['bloodstream', 'youtube'];
      platformValues.forEach(value => {
        const option = platformSelect.querySelector(`option[value="${value}"]`);
        if (option) {
          option.selected = true;
        }
      });
      // Update the multiselect plugin
      $(platformSelect).multiselect('reload');
    }

    // Step 2 - Content Information
    const titleField = this.el.container.querySelector('#title');
    if (titleField) {
      titleField.value = 'The Amazing Test Content';
      titleField.setAttribute('data-touched', 'true');
      titleField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Content type is now handled by feature_type in Step 1

    const synopsisField = this.el.container.querySelector('#synopsis');
    if (synopsisField) {
      synopsisField.value = 'In a world where automated testing meets cinematic excellence, one developer dares to fill forms with fake data. This groundbreaking documentary explores the intersection of software development and creative expression through the lens of form validation.';
      synopsisField.setAttribute('data-touched', 'true');
      synopsisField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Select some genres
    const genresSelect = this.el.container.querySelector('#genres-select');
    if (genresSelect && this.genresData) {
      // Select first 3 genres
      const genreIds = this.genresData.slice(0, 3).map(g => g.id.toString());
      $(genresSelect).multiselect('deselectAll', false);
      genreIds.forEach(id => {
        $(genresSelect).find(`option[value="${id}"]`).prop('selected', true);
      });
      $(genresSelect).multiselect('reload');

      // Clear error state
      const genresContainer = this.el.container.querySelector('#genres-container');
      if (genresContainer) {
        this.clearMultiselectError(genresContainer);
      }
    }

    const festivalsField = this.el.container.querySelector('#festivals');
    if (festivalsField) {
      festivalsField.value = 'Cannes Film Festival 2024 (Official Selection), Sundance Film Festival 2024 (Winner - Best Documentary)';
      festivalsField.setAttribute('data-touched', 'true');
      festivalsField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const awardsField = this.el.container.querySelector('#awards');
    if (awardsField) {
      awardsField.value = 'Academy Award Nominee - Best Original Screenplay, Golden Globe Winner - Best Motion Picture';
      awardsField.setAttribute('data-touched', 'true');
      awardsField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Add IMDB link
    const imdbField = this.el.container.querySelector('#imdbLink');
    if (imdbField) {
      imdbField.value = 'https://www.imdb.com/title/tt11692070/';
      imdbField.setAttribute('data-touched', 'true');
      imdbField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Step 3 - Review Materials
    const trailerLink = this.el.container.querySelector('#trailer_link');
    if (trailerLink) {
      trailerLink.value = 'https://vimeo.com/123456789';
      trailerLink.setAttribute('data-touched', 'true');
      trailerLink.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const featureLink = this.el.container.querySelector('#feature_link');
    if (featureLink) {
      featureLink.value = 'https://vimeo.com/987654321';
      featureLink.setAttribute('data-touched', 'true');
      featureLink.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const notesField = this.el.container.querySelector('#notes');
    if (notesField) {
      notesField.value = 'This is a test submission generated by the dev fill button. All data is fictional and for testing purposes only. Password for screeners: test123';
      notesField.setAttribute('data-touched', 'true');
      notesField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Step 4 - Distribution
    const territoriesSelect = this.el.container.querySelector('select[name="territories"]');
    if (territoriesSelect) {
      territoriesSelect.value = 'worldwide';
      territoriesSelect.setAttribute('data-touched', 'true');
      territoriesSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const rightsRadio = this.el.container.querySelector('input[name="available_rights"][value="all"]');
    if (rightsRadio) {
      rightsRadio.checked = true;
      rightsRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Already handled above

    const marketingInfo = this.el.container.querySelector('textarea[name="marketing_info"]');
    if (marketingInfo) {
      marketingInfo.value = 'Available marketing materials include: high-resolution posters, social media assets, behind-the-scenes footage, cast interviews, and a comprehensive press kit. Digital marketing campaign already in progress.';
      marketingInfo.setAttribute('data-touched', 'true');
      marketingInfo.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Step 5 - Contact Info
    const firstName = this.el.container.querySelector('#first_name');
    if (firstName) {
      firstName.value = 'John';
      firstName.setAttribute('data-touched', 'true');
      firstName.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const lastName = this.el.container.querySelector('#last_name');
    if (lastName) {
      lastName.value = 'Developer';
      lastName.setAttribute('data-touched', 'true');
      lastName.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const role = this.el.container.querySelector('#role');
    if (role) {
      role.value = 'Producer / Test Engineer';
      role.setAttribute('data-touched', 'true');
      role.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const email = this.el.container.querySelector('#email');
    if (email) {
      email.value = 'b+submissionAuto@debea.si';
      email.setAttribute('data-touched', 'true');
      email.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const phoneCountry = this.el.container.querySelector('#phone_country_code');
    if (phoneCountry) {
      phoneCountry.value = '+1';
      phoneCountry.setAttribute('data-touched', 'true');
      phoneCountry.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const phoneNumber = this.el.container.querySelector('#phone_number');
    if (phoneNumber) {
      phoneNumber.value = '555-123-4567';
      phoneNumber.setAttribute('data-touched', 'true');
      phoneNumber.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Step 6 - Agreement fields (fill before navigating to step 6)
    const agreementFirstName = this.el.container.querySelector('#agreement_first_name');
    if (agreementFirstName) {
      agreementFirstName.value = 'John';
      agreementFirstName.setAttribute('data-touched', 'true');
      agreementFirstName.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const agreementLastName = this.el.container.querySelector('#agreement_last_name');
    if (agreementLastName) {
      agreementLastName.value = 'Developer';
      agreementLastName.setAttribute('data-touched', 'true');
      agreementLastName.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const distributionAgreement = this.el.container.querySelector('#distribution_agreement');
    if (distributionAgreement) {
      distributionAgreement.checked = true;
      distributionAgreement.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const notReleased = this.el.container.querySelector('#not_released');
    if (notReleased) {
      notReleased.checked = true;
      notReleased.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Now validate each step and mark as complete
    setTimeout(() => {
      // Validate and mark each step as complete
      for (let stepNum = 1; stepNum <= 5; stepNum++) {
        // Validate the step without showing errors
        const validation = this.validateStep(stepNum, false);

        if (validation.valid) {
          // Mark the step as completed (this will make it green)
          this.wizard.markStepComplete(stepNum);

          // Also mark it as visited so it's clickable
          if (!this.wizard.visitedSteps.includes(stepNum)) {
            this.wizard.visitedSteps.push(stepNum);
          }
        } else {
          console.warn(`Step ${stepNum} validation failed:`, validation);
        }
      }

      // Update the wizard UI to reflect the completed steps
      this.wizard.updateStepClasses();

      // Now go to step 6 (review)
      this.wizard.goToStep(6);

      this.showNotification('Form filled with fake data and all steps validated!', 'success');

      // Reset the flag after navigation is complete
      setTimeout(() => {
        this.isDevFilling = false;
      }, 100);
    }, 1000);
  }

  applyFormConfig (config) {
    console.log('Applying form config:', config);

    // Hide platform selection if requested
    if (config.hide_platform) {
      const platformGroup = this.el.container.querySelector('#platform-selection-group');
      if (platformGroup) {
        platformGroup.style.display = 'none';

        // Update step 1 subtitle
        const step1Subtitle = this.el.container.querySelector('#step1-subtitle');
        if (step1Subtitle) {
          step1Subtitle.textContent = 'Select your production status';
        }

        // If platform is provided in prefill, set it as selected
        if (window.submissionPrefillData) {
          const platformValue = window.submissionPrefillData.desired_platform || window.submissionPrefillData.platform;
          if (platformValue) {
            // Parse platforms - could be comma-separated string or array
            let platformsArray = platformValue;
            if (!Array.isArray(platformsArray)) {
              if (typeof platformsArray === 'string' && platformsArray.includes(',')) {
                platformsArray = platformsArray.split(',').map(p => p.trim());
              } else {
                platformsArray = [platformsArray];
              }
            }

            const platformSelect = this.el.container.querySelector('#platform-select');
            if (platformSelect) {
              // Set all platform values
              platformsArray.forEach(platform => {
                const option = platformSelect.querySelector(`option[value="${platform}"]`);
                if (option) {
                  option.selected = true;
                }
              });

              // Update the multiselect plugin
              $(platformSelect).multiselect('reload');
            }

            // Don't show any visual indicator - just hide the platform selection completely
          }
        }
      }
    }

    // Handle other form configuration options here
    // Removed from_prelander visual indicator as per requirements
  }

  copyPrefillUrl () {
    try {
      // Save current step data first
      const currentStep = this.wizard ? this.wizard.getCurrentStep() : 1;
      this.saveStepData(currentStep);

      const formData = {};

      // Handle all text inputs, textareas, and selects
      this.el.form.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], input[type="url"], input[type="hidden"], textarea, select').forEach(field => {

        // Skip the genres multiselect - it's handled separately below
        if (field.id === 'genres-select' || field.name === 'genres[]') {
          return;
        }

        if (field.name && field.name.trim() && field.value && field.value.trim() && !field.classList.contains('no-validation')) {
          // Map phone_number to phone for backward compatibility
          if (field.name === 'phone_number') {
            formData['phone'] = field.value;
          } else if (field.name === 'phone_country_code') {
            formData['phone_country'] = field.value;
          } else {
            formData[field.name] = field.value;
          }
        }
      });

      // Handle radio buttons - get only checked ones
      this.el.form.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
        if (radio.name && radio.name.trim()) {
          formData[radio.name] = radio.value;
        }
      });

      // Handle checkboxes - get only checked ones
      const checkboxGroups = {};
      this.el.form.querySelectorAll('input[type="checkbox"]:checked').forEach(checkbox => {
        const name = checkbox.name;
        if (!name || !name.trim()) {
          return; // Skip checkboxes without names
        }
        if (name.endsWith('[]')) {
          // Array field - collect all values
          const cleanName = name.slice(0, -2); // Remove []
          if (!checkboxGroups[cleanName]) {
            checkboxGroups[cleanName] = [];
          }
          checkboxGroups[cleanName].push(checkbox.value);
        } else {
          // Single checkbox
          formData[name] = checkbox.value || 'on';
        }
      });

      // Add array checkbox values as comma-separated string for compatibility
      Object.keys(checkboxGroups).forEach(name => {
        // Skip genres as it's handled separately below
        if (name !== 'genres') {
          formData[name] = checkboxGroups[name].join(',');
        }
      });

      // Handle platform multi-select
      if (!formData['desired_platform']) {
        // First check saved form data
        if (this.formData && this.formData['desired_platform[]']) {
          const platforms = Array.isArray(this.formData['desired_platform[]'])
            ? this.formData['desired_platform[]']
            : [this.formData['desired_platform[]']];
          formData['desired_platform'] = platforms.join(',');
          console.log('Using stored desired_platform from formData:', formData['desired_platform']);
        } else {
          // Fallback: try to get from DOM if we're on step 1
          const platformSelect = document.querySelector('#platform-select');
          console.log('Platform select element:', platformSelect);

          if (platformSelect) {
            // Use jQuery method for multiselect
            const selectedPlatforms = $(platformSelect).val() || [];
            console.log('Selected platforms from multiselect:', selectedPlatforms);

            if (selectedPlatforms.length > 0) {
              formData['desired_platform'] = selectedPlatforms.join(',');
              console.log('Platforms added to formData:', formData['desired_platform']);
            }
          }
        }
      }

      // Handle genres multi-select separately
      // First check if we have saved form data with genres (regardless of current step)
      if (this.formData && this.formData.genres) {
        // Use stored form data - this ensures we get genres even when not on step 2
        const genres = Array.isArray(this.formData.genres) ? this.formData.genres : [this.formData.genres];
        console.log('Using stored genres from formData:', genres);
        formData['genres'] = genres.join(',');
      } else {
        // Fallback: try to get from DOM if we're on step 2
        const genresSelect = document.querySelector('#genres-select');
        console.log('Genres select element:', genresSelect);

        if (genresSelect) {
          // Always use jQuery method for multiselect
          const selectedGenres = $(genresSelect).val() || [];
          console.log('Selected genres from multiselect:', selectedGenres);

          if (selectedGenres.length > 0) {
            formData['genres'] = selectedGenres.join(',');
            console.log('Genres added to formData:', formData['genres']);
          }
        }
      }

      // Encode the form data as base64
      const jsonString = JSON.stringify(formData);
      const base64Data = btoa(jsonString);

      // Debug: Log all collected parameters
      console.log('Collected form data for prefill URL:', formData);
      console.log('JSON string:', jsonString);
      console.log('Base64 encoded:', base64Data);

      // Build the URL pointing to submit-title-intro
      const baseUrl = window.location.origin + '/submit-title-intro';
      const prefillUrl = baseUrl + '?data=' + encodeURIComponent(base64Data);

      console.log('Generated prefill URL:', prefillUrl);

      // Copy to clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(prefillUrl).then(() => {
          this.showNotification('Prefill URL copied to clipboard! Users will start at the intro page.', 'success');
          console.log('Prefill URL:', prefillUrl);
        }).catch(err => {
          console.error('Failed to copy:', err);
          // Fallback - show URL in prompt
          this.showPrefillUrlPrompt(prefillUrl);
        });
      } else {
        // Fallback for older browsers
        this.showPrefillUrlPrompt(prefillUrl);
      }
    } catch (error) {
      console.error('Error in copyPrefillUrl:', error);
      this.showNotification('Error copying prefill URL. Check console for details.', 'error');
    }
  }

  showPrefillUrlPrompt (url) {
    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.width = '2em';
    textarea.style.height = '2em';
    textarea.style.padding = '0';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.boxShadow = 'none';
    textarea.style.background = 'transparent';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.showNotification('Prefill URL copied to clipboard!', 'success');
      } else {
        prompt('Copy this URL:', url);
      }
    } catch (err) {
      prompt('Copy this URL:', url);
    }

    document.body.removeChild(textarea);
  }

  applyPrefillData (data) {
    console.log('Applying prefill data:', data);

    // Step 1: Content type
    if (data.feature_type) {
      const featureTypeRadio = this.el.container.querySelector(`input[name="feature_type"][value="${data.feature_type}"]`);
      if (featureTypeRadio) {
        featureTypeRadio.checked = true;
        featureTypeRadio.dispatchEvent(new Event('change', { bubbles: true }));

        if (data.feature_type === 'other' && data.feature_type_other && this.el.featureTypeOther) {
          this.el.featureTypeOther.classList.remove('hidden');
          this.el.featureTypeOther.value = data.feature_type_other;
          this.el.featureTypeOther.setAttribute('required', 'required');
        }
      }
    }

    // Step 1: Production phase
    if (data.production_phase) {
      const phaseRadio = this.el.container.querySelector(`input[name="production_phase"][value="${data.production_phase}"]`);
      if (phaseRadio) {
        phaseRadio.checked = true;
        phaseRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Step 1: Desired platforms for multi-select
    console.log('Applying desired_platform:', data.desired_platform, 'Is array?', Array.isArray(data.desired_platform));
    if (data.desired_platform) {
      // Parse platforms - it might come as comma-separated string from prefill
      let platformsArray = data.desired_platform;
      if (!Array.isArray(platformsArray)) {
        if (typeof platformsArray === 'string' && platformsArray.includes(',')) {
          platformsArray = platformsArray.split(',').map(p => p.trim());
        } else {
          platformsArray = [platformsArray];
        }
      }

      console.log('Processing platforms array:', platformsArray);

      // Wait for the platform multi-select to be initialized
      let attemptCount = 0;
      const checkPlatformInterval = setInterval(() => {
        attemptCount++;
        const platformSelect = $('#platform-select');
        console.log(`Attempt ${attemptCount}: Checking platform select...`, {
          found: platformSelect.length > 0,
          nextElement: platformSelect.next().attr('class'),
        });

        // Check if multiselect wrapper exists
        const msWrapper = platformSelect.next('.ms-options-wrap');
        if (platformSelect.length && msWrapper.length) {
          console.log('Platform multiselect wrapper found, setting values:', platformsArray);
          // Set the selected platforms
          platformSelect.val(platformsArray);
          // Trigger reload to update the multiselect UI
          platformSelect.multiselect('reload');

          // Check if "other" is selected and show the other input
          if (platformsArray.includes('other') && data.desired_platform_other) {
            const otherInput = document.querySelector('input[name="desired_platform_other"]');
            if (otherInput) {
              otherInput.classList.remove('hidden');
              otherInput.value = data.desired_platform_other;
              otherInput.setAttribute('required', 'required');
            }
          }

          clearInterval(checkPlatformInterval);
        } else if (attemptCount > 20) {
          console.error('Platform multiselect not initialized after 20 attempts');
          clearInterval(checkPlatformInterval);
        }
      }, 100);
    }

    // Step 2: Content information
    if (data.title) {
      const titleField = this.el.container.querySelector('#title');
      if (titleField) {
        titleField.value = data.title;
        titleField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // Content type is now handled by feature_type in Step 1

    if (data.synopsis) {
      const synopsisField = this.el.container.querySelector('#synopsis');
      if (synopsisField) {
        synopsisField.value = data.synopsis;
        synopsisField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (data.festivals) {
      const festivalsField = this.el.container.querySelector('#festivals');
      if (festivalsField) {
        festivalsField.value = data.festivals;
      }
    }

    if (data.awards) {
      const awardsField = this.el.container.querySelector('#awards');
      if (awardsField) {
        awardsField.value = data.awards;
      }
    }

    if (data.imdb_link) {
      const imdbField = this.el.container.querySelector('#imdbLink');
      if (imdbField) {
        imdbField.value = data.imdb_link;
        // Trigger input event to activate auto-fetch if enabled
        imdbField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // Step 3: Review materials
    if (data.trailer_link) {
      const trailerLinkField = this.el.container.querySelector('#trailer_link');
      if (trailerLinkField) {
        trailerLinkField.value = data.trailer_link;
        // Show the link fields container
        const trailerContainer = document.getElementById('trailer-link-fields');
        const trailerButton = document.querySelector('[data-target="trailer-link-fields"]');
        if (trailerContainer) {
          trailerContainer.classList.remove('hidden');
          if (trailerButton) {
            trailerButton.setAttribute('aria-expanded', 'true');
            trailerButton.style.display = 'none';
          }
        }
      }
    }

    if (data.trailer_password) {
      const trailerPasswordField = this.el.container.querySelector('#trailer_password');
      if (trailerPasswordField) {
        trailerPasswordField.value = data.trailer_password;
      }
    }

    if (data.feature_link) {
      const featureLinkField = this.el.container.querySelector('#feature_link');
      if (featureLinkField) {
        featureLinkField.value = data.feature_link;
        // Show the link fields container
        const featureContainer = document.getElementById('feature-link-fields');
        const featureButton = document.querySelector('[data-target="feature-link-fields"]');
        if (featureContainer) {
          featureContainer.classList.remove('hidden');
          if (featureButton) {
            featureButton.setAttribute('aria-expanded', 'true');
            featureButton.style.display = 'none';
          }
        }
      }
    }

    if (data.feature_password) {
      const featurePasswordField = this.el.container.querySelector('#feature_password');
      if (featurePasswordField) {
        featurePasswordField.value = data.feature_password;
      }
    }

    if (data.notes) {
      const notesField = this.el.container.querySelector('#notes');
      if (notesField) {
        notesField.value = data.notes;
      }
    }

    // Step 4: Distribution details
    if (data.territories) {
      if (this.el.territoriesSelect) {
        this.el.territoriesSelect.value = data.territories;
        this.el.territoriesSelect.dispatchEvent(new Event('change', { bubbles: true }));

        if (data.territories === 'other' && data.territories_other && this.el.territoriesOther) {
          this.el.territoriesOther.value = data.territories_other;
        }
      }
    }

    if (data.available_rights) {
      const rightsRadio = this.el.container.querySelector(`input[name="available_rights"][value="${data.available_rights}"]`);
      if (rightsRadio) {
        rightsRadio.checked = true;
        rightsRadio.dispatchEvent(new Event('change', { bubbles: true }));

        if (data.available_rights === 'some' && data.rights_some_details && this.el.rightsSomeDetails) {
          this.el.rightsSomeDetails.classList.remove('hidden');
          this.el.rightsSomeDetails.value = data.rights_some_details;
        }
      }
    }

    if (data.marketing_info) {
      const marketingField = this.el.container.querySelector('#marketing_info');
      if (marketingField) {
        marketingField.value = data.marketing_info;
      }
    }

    // Step 5: Contact information
    if (data.first_name) {
      const firstNameField = this.el.container.querySelector('#first_name');
      if (firstNameField) {
        firstNameField.value = data.first_name;
        firstNameField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (data.last_name) {
      const lastNameField = this.el.container.querySelector('#last_name');
      if (lastNameField) {
        lastNameField.value = data.last_name;
        lastNameField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (data.role) {
      const roleField = this.el.container.querySelector('#role');
      if (roleField) {
        roleField.value = data.role;
      }
    }

    if (data.email) {
      const emailField = this.el.container.querySelector('#email');
      if (emailField) {
        emailField.value = data.email;
        emailField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (data.phone_country_code) {
      const phoneCountryField = this.el.container.querySelector('#phone_country_code');
      if (phoneCountryField) {
        phoneCountryField.value = data.phone_country_code;
        phoneCountryField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (data.phone_number) {
      const phoneNumberField = this.el.container.querySelector('#phone_number');
      if (phoneNumberField) {
        phoneNumberField.value = data.phone_number;
        phoneNumberField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // Step 6: Agreement fields
    if (data.agreement_first_name) {
      const agreementFirstNameField = this.el.container.querySelector('#agreement_first_name');
      if (agreementFirstNameField) {
        agreementFirstNameField.value = data.agreement_first_name;
        agreementFirstNameField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (data.agreement_last_name) {
      const agreementLastNameField = this.el.container.querySelector('#agreement_last_name');
      if (agreementLastNameField) {
        agreementLastNameField.value = data.agreement_last_name;
        agreementLastNameField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (data.distribution_agreement === 'on') {
      const agreementCheckbox = this.el.container.querySelector('#distribution_agreement');
      if (agreementCheckbox) {
        agreementCheckbox.checked = true;
        agreementCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Handle not_released checkbox
    if (data.not_released === 'on') {
      const notReleasedCheckbox = this.el.container.querySelector('#not_released');
      if (notReleasedCheckbox) {
        notReleasedCheckbox.checked = true;
        notReleasedCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Handle genres after they're loaded
    if (data.genres) {
      console.log('Genres data type:', typeof data.genres, 'Value:', data.genres);

      // Ensure genres is an array (it might come as comma-separated string from prefill)
      let genresArray = data.genres;
      if (!Array.isArray(genresArray)) {
        if (typeof genresArray === 'string' && genresArray.includes(',')) {
          genresArray = genresArray.split(',').map(g => g.trim());
        } else {
          genresArray = [genresArray];
        }
      }

      console.log('Attempting to prefill genres:', genresArray);
      // Wait for genres to be initialized
      let attemptCount = 0;
      const checkGenresInterval = setInterval(() => {
        attemptCount++;
        const genresSelect = $('#genres-select');
        console.log(`Attempt ${attemptCount}: Checking genres select...`, {
          found: genresSelect.length > 0,
          hasJqmsLoaded: genresSelect.hasClass('jqmsLoaded'),
          classes: genresSelect.attr('class'),
          nextElement: genresSelect.next().attr('class'),
        });

        // Check if multiselect wrapper exists
        const msWrapper = genresSelect.next('.ms-options-wrap');
        if (genresSelect.length && msWrapper.length) {
          console.log('Multiselect wrapper found, setting values:', genresArray);
          // Set the selected genres
          genresSelect.val(genresArray);
          // Trigger reload to update the multiselect UI
          genresSelect.multiselect('reload');

          // Clear error state
          const genresContainer = document.querySelector('#genres-container');
          if (genresContainer) {
            this.clearMultiselectError(genresContainer);
          }

          clearInterval(checkGenresInterval);
        }
      }, 100);

      // Stop checking after 5 seconds
      setTimeout(() => {
        clearInterval(checkGenresInterval);
        if (attemptCount > 0) {
          console.log('Stopped checking for genres after', attemptCount, 'attempts');
        }
      }, 5000);
    }

    // Navigate to specific step if provided
    if (data.step && this.wizard) {
      const stepNum = parseInt(data.step);
      if (stepNum >= 1 && stepNum <= 6) {
        // Wait a bit for all fields to be populated before changing step
        setTimeout(() => {
          // Mark all previous steps as completed and visited when navigating directly to a step
          for (let i = 1; i < stepNum; i++) {
            if (!this.wizard.completedSteps.includes(i)) {
              this.wizard.completedSteps.push(i);
            }
            if (!this.wizard.visitedSteps.includes(i)) {
              this.wizard.visitedSteps.push(i);
            }
            // Save form data for each step
            this.saveStepData(i);
          }

          // Mark the current step as visited too
          if (!this.wizard.visitedSteps.includes(stepNum)) {
            this.wizard.visitedSteps.push(stepNum);
          }

          // Update the wizard UI to reflect completed steps
          this.wizard.updateStepClasses();

          // Navigate to the requested step
          this.wizard.goToStep(stepNum);
        }, 500);
      }
    }

    // Update button states after prefilling
    setTimeout(() => {
      this.updateButtonStates();
    }, 100);
  }

  // CAP (Completely Automated Public Turing test) Methods
  initializeCAP () {
    if (!window.capConfig || !window.capConfig.enabled) {
      console.log('ℹ️ CAP Bot Protection: Disabled');
      return;
    }

    console.log('%c🔐 CAP Bot Protection Active', 'color: #4CAF50; font-weight: bold; font-size: 14px;');
    console.log('Challenge:', window.capConfig.challenge.substring(0, 20) + '...');
    console.log('Difficulty:', window.capConfig.difficulty, 'leading zeros required');

    // Initialize CAP solver
    this.capSolver = new CapSolver({
      enabled: window.capConfig.enabled,
      challenge: window.capConfig.challenge,
      difficulty: window.capConfig.difficulty,
      solutionFieldId: 'capSolution',
      statusElementId: 'cap-status-text',
      progressBarId: 'cap-progress-bar',
      attemptsElementId: 'cap-attempts',
      onComplete: function(result) {
        console.log('%c✅ Solution found!', 'color: #4CAF50; font-weight: bold;');
        console.log('Nonce:', result.nonce);
        console.log('Hash:', result.hash);
        console.log('Time:', result.elapsed + 's');

        const statusText = document.getElementById('cap-status-text');
        const icon = document.getElementById('cap-icon');
        const progressBar = document.getElementById('cap-progress-bar');

        if (statusText) {
          statusText.innerHTML = 'Bot Protection: <span style="color: var(--theme-success);">Verified</span>';
        }
        if (icon) {
          icon.className = 'fas fa-check-circle';
          icon.style.color = 'var(--theme-success)';
        }
        if (progressBar) {
          progressBar.style.background = 'var(--theme-success)';
        }
      },
      onProgress: function(progress) {
        if (progress.nonce % 5000 === 0) {
          console.log('🔄 Attempt', progress.nonce.toLocaleString() + '...', 'Hash:', progress.hash.substring(0, 10) + '...');
        }
      },
      onError: function(error) {
        console.error('❌ CAP solver error:', error.message);
      },
    });

    // Start solving immediately in background (from Step 1)
    console.log('⏰ Starting CAP solver in background...');
    this.capSolver.autoSolve(1000);

    // Show status on Step 6
    setTimeout(() => {
      const wizard = this.wizard;
      const self = this;

      if (wizard) {
        // Listen for step changes
        const originalGoToStep = wizard.goToStep;

        wizard.goToStep = function(step) {
          originalGoToStep.call(wizard, step);

          // When reaching final step (6), show CAP status
          if (step === 6) {
            const capStatus = document.getElementById('cap-status');
            if (capStatus) {
              capStatus.style.display = 'block';
            }

            // Update UI based on solver state
            if (self.capSolver) {
              const progressContainer = document.getElementById('cap-progress-container');
              const icon = document.getElementById('cap-icon');

              if (self.capSolver.isSolved()) {
                // Already solved - show success
                if (progressContainer) {
                  progressContainer.style.display = 'none';
                }
              } else {
                // Still solving - show progress
                if (progressContainer) {
                  progressContainer.style.display = 'block';
                }
                if (icon) {
                  icon.className = 'fas fa-cog fa-spin';
                }
              }
            }
          }
        };
      }
    }, 1000);
  }
};

// Initialize module when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Only initialize if the container element exists
  var container = document.querySelector('#titleSubmissionContainer');
  if (container !== null) {
    window.sflow = window.sflow || {};

    // Add a small delay to ensure all modules are loaded
    setTimeout(function() {
      try {
        window.sflow.filmSubmission = new SubmitTitle();
      } catch (e) {
        alert('Error initializing SubmitTitle: ' + e.message);
      }
    }, 100);
  }
});